import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ordersService, {
  type OrderDetails,
  type InvoiceLineItem,
} from '../../../services/orders.service';
import { useChangeAll } from '../changeAll/ChangeAllContext';
import { useChangeAllFields } from '../../../hooks/useChangeAllFields';
import { invoicingFieldArm, invoicingFieldViewKeys } from '../changeAll/fanout';
import {
  type InvoiceLineItemCreate,
  type InvoiceLineItemBatchItem,
} from '../../../services/invoiceLineItems.service';
import { Row, Label, Section, Icon } from './orderform';
import DateTimePicker from '../../../components/DateTimePicker';
import { parseMdYHm, formatMdYHm, formatMdYDate } from '../../../utils/dateUtils';
import { toBackendDateValue } from '../../../utils/buildOrderDataRows';
import PermissionsContainer, { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import { isValidEmailList } from '../../../utils/email';
import { forcedSelectOption } from '../../../utils/forcedSelectOption';
import { listContacts, type Contact } from '../../../services/contacts.service';
import OrderLcFieldGroup from './OrderLcFieldGroup';
import { useCanEditOrderLcFields } from '../useCanEditOrderLcFields';
import {
  useCanApproveInvoice,
  INVOICE_APPROVAL_COMPONENT_ID,
  INVOICE_APPROVAL_COMPONENT_NAME,
} from '../useCanApproveInvoice';
import {
  useCanEditBillFrom,
  BILL_FROM_COMPONENT_ID,
  BILL_FROM_COMPONENT_NAME,
} from '../useCanEditBillFrom';
import { isTemplatePronumber, isTemplateApproved, templateFieldEditable } from '../templateFieldPolicy';
import BillToLookupField from '../../../components/BillToLookupField';
import EntityEditLink from '../../../components/EntityEditLink';
import { useOrderFieldDisplayContext } from '../OrderFieldDisplayContext';
import { useTryPermissions } from '../../../contexts/PermissionContext';
import {
  getInvoiceHoldReasons,
  getInvoiceLiTypes,
  type InvoiceLiType,
} from '../../../services/orderLookups.service';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import quoteService, { type FscResult, type QuoteLookupContext } from '../../../services/quote.service';
import { isWarehouseQuoteError, WAREHOUSE_QUOTE_MESSAGE } from '../../../services/quoteErrors';
import { type TariffRateRow, type QuoteListRow } from '../../order-management/fixtures';
import QuoteLaneEditorDrawer from '../../order-management/components/QuoteLaneEditorDrawer';
import { fscRateUnit } from '../../order-management/components/useQuoteFsc';
import {
  suggestionsFromEstimate,
  detectDrayRateMismatch,
  findBaseDrayItem,
  type ChargeSuggestion,
} from './invoiceChargeRules';
import { resolveFscDate, toIsoDate } from './invoiceFscDate';
import {
  computeLineTotal,
  estimateStagedChanges,
  formatQuoteRate,
  RATE_TYPE_OPTIONS,
  isPctRateType,
  whseGlCodeMatches,
  type StagedChange,
} from './invoiceLineItemShared';
import RebillCreditListModal from './RebillCreditListModal';
import RebillCreditEditModal from './RebillCreditEditModal';
import { rebillCreditService } from '../../../services/rebillCredit.service';
import AccessorialEditModal from './AccessorialEditModal';
import { accessorialsService, type AccessorialListRow } from '../../../services/accessorials.service';
import {
  invoiceEstimateService,
  invoiceOverrideService,
  isMainInvoiceLine,
  type InvoiceEstimate,
  type EstimatedLine,
  type InvoiceProjection,
  type LineItemOverride,
} from '../../../services/invoiceReconcile.service';
import InvoiceEstimateModal, { ReasonText, type InvoiceLineGroup } from './InvoiceEstimateModal';
import LineItemOverrideModal from './LineItemOverrideModal';
import OverrideEditButton from './OverrideEditButton';
import QuoteLookupField from '../../../components/QuoteLookupField';
import InvoiceOptionsButton from './InvoiceOptionsButton';
import './invoicingView.css';

/**
 * Component-permission identifiers for invoicing write actions (Add Line,
 * Save Row, Delete Row, Save Invoice Fields). PermissionsContainer keys off
 * these so admins can hide the buttons in production via the Permissions
 * Mode cog overlay; when access resolves to 0 the wrapped button hides at
 * runtime.
 */
export const INVOICE_WRITE_PERMISSION_ID = 'pro-invoicing-write';
export const INVOICE_WRITE_PERMISSION_NAME = 'Pro Invoicing — Header / Line Item Add/Edit';

/**
 * Gate for the add/remove-line + add-accessorial write controls. On a real ORDER
 * they sit behind the `pro-invoicing-write` component permission; on a TEMPLATE the
 * template-edit gate (canEditTemplate) is the sole authority, so render the children
 * bare (mirrors OrderLcFieldGroup's template branch) — never behind an order-only
 * permission the template editor may not hold. Module-scope (NOT defined inside the
 * view) so it keeps a stable component identity across re-renders — an in-render
 * definition remounts its subtree every render, detaching buttons mid-interaction.
 */
const InvoiceWriteGate: React.FC<{
  isTemplate: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ isTemplate, className, children }) =>
  isTemplate ? (
    // Keep the caller's layout class (e.g. the remove-✕ cell's inline-flex) so the
    // template control aligns the same as the order one, which gets it via the
    // PermissionsContainer wrapper below.
    className ? <span className={className}>{children}</span> : <>{children}</>
  ) : (
    <PermissionsContainer
      componentId={INVOICE_WRITE_PERMISSION_ID}
      componentName={INVOICE_WRITE_PERMISSION_NAME}
      className={className}
    >
      {children}
    </PermissionsContainer>
  );
/**
 * Gate for the Approve / By / Date control. On a real ORDER it sits behind the
 * `invoice-approval` component permission — the port of V1's field ACL on `approvedby`
 * (see useCanApproveInvoice) — and on a TEMPLATE it renders bare, because the
 * template-edit gate is the sole authority there (same rule as InvoiceWriteGate).
 *
 * `defaultDisabled`, not `defaultHidden`: an un-granted user still SEES who approved an
 * invoice and when. Only the checkbox locks, via `canToggleApproval`; this wrapper
 * anchors the frame + self-registration, it does not disable anything itself.
 *
 * Module-scope for the same reason as InvoiceWriteGate — an in-render definition would
 * remount the subtree every render.
 */
const InvoiceApprovalGate: React.FC<{
  isTemplate: boolean;
  children: React.ReactNode;
}> = ({ isTemplate, children }) =>
  isTemplate ? (
    <>{children}</>
  ) : (
    <PermissionsContainer
      componentId={INVOICE_APPROVAL_COMPONENT_ID}
      componentName={INVOICE_APPROVAL_COMPONENT_NAME}
      defaultDisabled
    >
      {children}
    </PermissionsContainer>
  );
// Same componentId as before — regrants aren't needed, the surface just got broader.
export const RECONCILE_PERMISSION_ID = 'reconcile-invoice';
export const RECONCILE_PERMISSION_NAME = 'Pro Invoicing — Invoice Reconciliation (on-demand)';
/** Margin-approval control (Field 1) — its own gate, separate from the LC-edit
 *  group; hidden until granted. */
export const MARGIN_APPROVAL_PERMISSION_ID = 'invoice-margin-approval';
export const MARGIN_APPROVAL_PERMISSION_NAME = 'Pro Invoicing — Margin Approval';
/** COD override (Field 3) — its own gate; read-only until granted. */
export const COD_PERMISSION_ID = 'invoice-cod';
export const COD_PERMISSION_NAME = 'Pro Invoicing — COD';
/** Do Not Bill Reason — manager-only, so it gets its OWN gate rather than riding
 *  the group that opens Bill From (which now has its own gate too — see
 *  useCanEditBillFrom; these stay separate, picking the reason is the tighter
 *  call). Ships **defaultDisabled**: an un-granted
 *  group still SEES why an order is not being billed, but cannot change it, and
 *  only an explicit Edit (2) grant opens the dropdown. The row itself only
 *  renders when Bill From is 'DO NOT BILL' (see `isDoNotBill`), so this gate is
 *  about who may pick the reason, not who may see that a reason exists.
 *  TEMPLATES are exempt — the template-edit gate is the sole authority there
 *  (same rule as InvoiceWriteGate), so `fieldEditable` short-circuits this. */
export const DO_NOT_BILL_REASON_PERMISSION_ID = 'invoice-do-not-bill-reason';
export const DO_NOT_BILL_REASON_PERMISSION_NAME = 'Pro Invoicing — Do Not Bill Reason';
/** Quote # (the v1-quote link) — its own gate, same Exempt scheme as
 *  lc-return-target-date: exactly Edit (2) = editable AND required at save,
 *  Exempt (3, also the un-granted default) = editable without the requirement,
 *  View/Hidden = read-only/hidden. The save-time "Quote # is required." rule
 *  in useOrderEditing keys off the SAME resolved level (via
 *  ProDetailContainer), so anyone the rule blocks can always fix it here. */
export const QUOTE_NUMBER_PERMISSION_ID = 'invoice-quote-number';
export const QUOTE_NUMBER_PERMISSION_NAME = 'Pro Invoicing — Quote #';
export const ACCESS_LEVEL_EDIT = 2;
export const ACCESS_LEVEL_EXEMPT = 3;

/** setExtraSaveErrors source key for the invalid-invoice-line-item hard block —
 *  a MAIN-invoice line whose Item isn't a real invoice_litypes entry (renders
 *  orange) can't be saved while the invoice date is blank. */
const INVOICE_LINE_ITEM_INVALID_ERROR = 'invoice-line-items-invalid';

/** setExtraSaveErrors source key for the blank-Item hard block — a line with NO Item
 *  bills money under no charge name. Separate from the invalid-Item key so the two
 *  blocks lift independently; they describe different mistakes and one can be fixed
 *  while the other stands. */
const INVOICE_LINE_ITEM_BLANK_ERROR = 'invoice-line-items-blank';

/**
 * Invoicing tab — now supports editing for key header fields and full CRUD
 * for invoice line items when `pronumber` is supplied.
 *
 * Layout mirrors:
 *   - top:    `@edit_layout_invoicing_header` (cms-master/lib/defs.pl:2613-2625)
 *   - bottom: the line-items table from `doInvoicingLineItem` and the
 *             auxiliary panel from `doInvoicingInfo` (cms-master/lib/lib_editrecord.pl)
 *
 * C1 editable header fields (V1 lib_editrecord.pl:3362-3381):
 *   invoicecomments, donotbillreason, billfromsystem, rebill, ratecomments,
 *   marginapprovedby, missinginvoiceapprovedby/reason
 *   (creditamt is read-only here — credits are raised via Rebill/Credit records —
 *    but creditreason stays editable so a mis-picked reason can be corrected)
 *
 * C2 line item CRUD (V1 lib_editrecord.pl:16635 `processSaveInvoiceLI`).
 */
/** Order-editing surface InvoicingView needs. All header-field edits flow
 *  through editedOrderFields (so they persist across tab switches and commit on
 *  the footer Save); the line-item draft is mirrored up via
 *  setInvoiceLineItemsDraft and restored from invoiceLineItemsDraft on remount. */
export interface InvoicingEditing {
  editedOrderFields: Record<string, any>;
  updateOrderField: (name: string, value: any) => void;
  setInvoiceLineItemsDraft?: (items: InvoiceLineItemBatchItem[], dirty: boolean) => void;
  invoiceLineItemsDraft?: InvoiceLineItemBatchItem[] | null;
  /** Push keyed validation errors that hard-block the footer Save (mirrors the
   *  hook's setExtraSaveErrors). Used to stop a save while a main-invoice line
   *  item carries an Item that isn't a real invoice_litypes entry. */
  setExtraSaveErrors?: (source: string, errors: string[]) => void;
  /** Bumps after each successful footer save — drives the Change-All fan-out. */
  saveVersion?: number;
  /** The open form's quote-match context, handed to the Quote # spyglass so it
   *  matches on STAGED values (rate zone, bill-to, delivery leg) the way V1's
   *  did — see useOrderEditing.quoteLookupContext. */
  quoteLookupContext?: QuoteLookupContext | null;
}

export interface InvoicingViewProps {
  details: Partial<OrderDetails> | null;
  /** When a numeric order pronumber is provided the view becomes editable and
   *  CRUD buttons appear. A "T"-prefixed template pronumber (string) is always
   *  read-only — its line items come straight from the template-aware details
   *  payload and there are no invoice-editing paths for templates. */
  pronumber?: number | string;
  /** Order-editing hook subset. On a template it powers the approve/unapprove
   *  toggle (the only editable control on the template Invoicing tab); the
   *  stamp is committed through the normal footer Save. */
  editing?: InvoicingEditing;
  /** CSR/LC template-edit gate. Gates the approve/unapprove toggle on a
   *  template. Defaults to editable; ignored for a normal order. */
  canEditTemplate?: boolean;
  /** Optional callback so parents can refresh their copy of orderDetails. */
  onSaved?: () => void;
  /** Create New Order mode: the drawer sits on the 'NEW' sentinel (no numeric
   *  pronumber), so the header + line items must open on the staged draft, while
   *  the accessorial section is hidden — the server copies the template's
   *  accessorials inside the create transaction (V1 gated on `!isneworder`). */
  createMode?: boolean;
}

const PLACEHOLDER = '—';
const LABEL_W = 132;
const LABEL_W_RIGHT = 124;

// V1 fv_marginlimit fallback defaults (libfast_cms.pl:54-55): the default margin limit shown
// next to "Margin Limit" when the order's marginlimitlocal is 0 — Local (23) normally, OTR (17)
// when the order is OTR. Display-only hint, mirroring V1's "Local Limit / OTR Limit:" extra.
const MARGIN_LIMIT_LOCAL_DEFAULT = 23;
const MARGIN_LIMIT_OTR_DEFAULT = 17;

interface ReadValueProps {
  value?: unknown;
  mono?: boolean;
  width?: number;
  ariaLabel?: string;
}
const ReadValue: React.FC<ReadValueProps> = ({ value, mono, width, ariaLabel }) => {
  const text = value == null || value === '' ? PLACEHOLDER : String(value);
  const isEmpty = text === PLACEHOLDER;
  const cls = [
    'inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate',
    '',
    isEmpty ? 'text-slate-400 dark:text-gray-500' : 'text-slate-900 dark:text-gray-100',
    width ? '' : 'flex-1 min-w-0',
  ]
    .filter(Boolean)
    .join(' ');
  const style = width ? { width: `${width}px` } : undefined;
  return (
    <span className={cls} style={style} aria-label={ariaLabel} title={isEmpty ? undefined : text}>
      {text}
    </span>
  );
};

const Divider: React.FC = () => (
  <div className="my-1.5 border-t border-dashed border-slate-200 dark:border-gray-700" />
);

/** Read-only comment box off the linked quote (Comments / Internal Notes). */
const QuoteCommentBox: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0 flex flex-col flex-1">
    <div className="flex items-center gap-1.5 mb-1">
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
        {label}
      </span>
    </div>
    <span
      className={
        'flex-1 block min-w-0 overflow-auto text-[0.75rem] whitespace-pre-wrap break-words ' +
        'border border-slate-200 dark:border-gray-700 rounded px-1.5 py-1 min-h-[44px] bg-slate-50 dark:bg-gray-800 ' +
        (value ? 'text-slate-900 dark:text-gray-100' : 'text-slate-400 dark:text-gray-500 italic')
      }
      aria-label={label}
    >
      {value || PLACEHOLDER}
    </span>
  </div>
);

const TH: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <th
    scope="col"
    className={
      'text-left text-[0.65625rem] font-semibold uppercase tracking-wider ' +
      'text-slate-500 dark:text-gray-400 px-2 py-1 border-b border-slate-200 dark:border-gray-700 whitespace-nowrap ' +
      (className ?? '')
    }
  >
    {children}
  </th>
);

/**
 * Invoice → Sage-export highlight (V1 formatview coloring, formatview.pl:5350-5351).
 * A filled RED banner once a record is invoiced (has an invoicedate) but not yet
 * exported to Sage, flipping to a filled near-BLACK once a masexportdate exists.
 * Empty string before an invoice date exists. masexportdate wins — once exported
 * we no longer flag "awaiting export". Used for both the main invoice's
 * Invoice/Sage-Date cell and each accessorial line.
 *
 * The red is `bg-brand-red rounded` + white text — the same brand-red status
 * highlight the Addl Cost tab's per-diem past-due row uses, so the two surfaces
 * stay visually consistent. The exported state uses `bg-neutral-900` (not pure
 * black) so it stays visible against the dark-mode card (gray-950) and table
 * (gray-800) backgrounds; text is forced white for contrast on the fill.
 */
const invoiceExportHighlight = (
  invoiceDate?: string | null,
  masExportDate?: string | null,
): { cls: string; title: string } => {
  if (masExportDate && masExportDate.trim()) {
    return {
      cls: 'bg-neutral-900 text-white rounded [&_*]:!text-white',
      title: 'Exported to Sage',
    };
  }
  if (invoiceDate && invoiceDate.trim()) {
    return {
      cls: 'bg-brand-red text-white rounded [&_*]:!text-white',
      title: 'Invoiced (not yet exported to Sage)',
    };
  }
  return { cls: '', title: '' };
};

/**
 * Required-for-Invoicing flags — editable N/Y/RESOLVED dropdowns (LC-edit group)
 * whose cells highlight yellow when set to 'Y' (Order-Info tank/scale parity).
 * `key` is the editedOrderFields / hv() key; requirePerDiemApproval is an
 * ordersall field, the rest are on the invoice table (hv resolves both).
 */
const REQUIRED_INVOICE_FIELDS: ReadonlyArray<{ label: string; key: string }> = [
  { label: 'Invoice Hold', key: 'requireRevisedDo' },
  { label: 'POD', key: 'requirePod' },
  { label: 'BOL', key: 'requireBol' },
  { label: 'EDI', key: 'requireEdi' },
  { label: 'MR/PL', key: 'requireMrpl' },
  { label: 'EIR In', key: 'requireInInterchange' },
  { label: 'EIR Out', key: 'requireOutInterchange' },
  { label: 'Chass EIR In', key: 'requireChassisInEir' },
  { label: 'Chass EIR Out', key: 'requireChassisOutEir' },
  { label: 'Per Diem App', key: 'requirePerDiemApproval' },
  { label: 'Pending Approval', key: 'pendingApproval' },
];

const money = (n: number | null | undefined): string => {
  if (n == null) return PLACEHOLDER;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── compact inline input classes ─────────────────────────────────────────────
const INPUT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-[3px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 focus:outline-none focus:ring-1 focus:ring-blue-400';
const SELECT_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 py-[2px] ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 h-6 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400';
const TEXTAREA_CLS =
  'min-w-0 flex-1 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 ' +
  'text-[0.75rem] text-slate-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y';

// V1 GL-code options (hardcoded list from the legacy invoicing UI), shown as a
// dropdown on each editable invoice line item. REGION/SINGLE/WAREHOUSE are the
// invoice_litypes.gltype values auto-filled when an Item is picked.
/** Component gating the GL Code dropdown once an invoice is out but not yet exported
 *  to Sage. Declared here rather than inline so the migration that seeds the grant and
 *  the container that reads it cannot drift apart on a typo — an id mismatch fails
 *  SILENTLY, as `defaultHidden` simply hides the control for everybody. */
const GL_CODE_COMPONENT_ID = 'invoice-gl-code';
const GL_CODE_COMPONENT_NAME = 'Pro Invoicing — GL Code after invoicing';

const GL_CODE_OPTIONS = [
  'GWI05-06', 'GWI08A', 'GWI08B', 'GWI09-10', 'GWI11', 'GWI13', 'GWI14', 'GWI16',
  'GWI17', 'GWI18', 'GWI20', 'GWI22', 'GWI27', 'GWI28', 'GWIC', 'REGION', 'SINGLE',
  'WAREHOUSE',
];

// RATE_TYPE_OPTIONS / isPctRateType / computeLineTotal live in
// ./invoiceLineItemShared (shared with the Accessorial modal); imported above.

// ── line-item editing state ───────────────────────────────────────────────────
interface EditingRow extends InvoiceLineItemCreate {
  /** undefined = new (not yet saved), otherwise the recno of the persisted row */
  recno: number | undefined;
  /** local key for React rendering */
  _key: string;
  /**
   * The item type this row held when it was last loaded from the server — NOT the
   * one on screen.
   *
   * Only the client knows this, and the Change All fan-out needs it: it matches a
   * target line by position and then checks the item type to be sure it is the same
   * charge. Checking the NEW type made retyping a charge across a group the one edit
   * the feature refused — every target still held the old type, so every target was
   * reported as a mismatch. Undefined on a row that was never saved.
   */
  originalItem?: string;
}

/** The inline "+ Add to Invoice" button. AMBER when the engine is actively suggesting
 *  the charge, BLUE for an ordinary add the user is initiating themselves. */
const ADD_CHARGE_BTN =
  'whitespace-nowrap rounded border border-blue-300 bg-blue-50 px-1.5 py-0 text-[0.6875rem] font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50';
const ADD_CHARGE_BTN_SUGGESTED =
  'whitespace-nowrap rounded border border-amber-400 bg-amber-100 px-1.5 py-0 text-[0.6875rem] font-medium text-amber-900 hover:bg-amber-200 dark:border-amber-600 dark:bg-amber-800/50 dark:text-amber-100 dark:hover:bg-amber-800';

/** The two BASE-PRICING charges. Unlike every other row in Quote Charges these are not
 *  quote ADDITIONAL charges: the dray rate lives on the quote HEADER and the fuel
 *  surcharge is computed, so neither carries an `additionalcharge_recno` to map through.
 *  Their line-item type is resolved by NAME instead (cms.invoice_litypes 6 and 12). */
const BASE_DRAY_ITEM = 'CONTAINER DRAY';
const BASE_FUEL_ITEM = 'FUEL SURCHARGE';

let _keyCounter = 0;
const nextKey = () => `li-${++_keyCounter}`;

const emptyRow = (): EditingRow => ({
  recno: undefined,
  _key: nextKey(),
  lineNumber: 0,
  item: '',
  glCode: '',
  qty: 0,
  rate: 0,
  rateType: '',
  description: '',
  total: 0,
});

/** Next sequential invoice line number: highest existing + 1 (0 when empty). */
export const nextLineNumber = (
  rows: ReadonlyArray<{ lineNumber: number }>,
): number => rows.reduce((max, r) => Math.max(max, r.lineNumber), -1) + 1;

/**
 * Fold an Invoice Estimate into the line-item rows — the "Stage in form" transform.
 *
 * Staged rows are highlighted blue and persist through the normal Save Changes, which
 * is how the estimate "applies" in cmsapi/shadow without a direct reconciler write.
 *
 * THE RULE THAT MATTERS: an ESTIMATED line stages with a BLANK rate. Its figures are
 * projected from today because the order isn't fully dated yet — showing that projection
 * is the whole point of the estimate, but writing it onto a customer's invoice would be
 * billing a guess. The line still lands (item + GL code + qty) so the charge is visibly
 * pending and someone fills in the rate once the real dates arrive.
 *
 * THE SECOND RULE: only the MAIN invoice's lines stage. The estimate projects every
 * invoice on the order, and these rows are the main invoice's form — so an accessorial
 * line has no row here to update (its recno isn't in `currentRows`, and both the update
 * and remove branches below correctly no-op) but an ADD would have fallen through to the
 * append and billed that charge on the WRONG DOCUMENT, to a party who frequently isn't
 * the order's billto at all. The modal shows accessorials read-only for the same reason.
 *
 * Pure, and exported, so both rules are testable without driving the modal.
 */
export interface OverrideCallout {
  item: string;
  reason: string;
  /** What the engine wanted — the figure the override DECLINED. On a rate correction
   *  that is the quote's price for the charge; on a suppressed charge it is what the
   *  charge would have been worth. */
  withheldTotal: number | null;
  /** What the invoice actually bills for this item. Null when nothing does, which is
   *  every declined charge. */
  billedTotal: number | null;
  /** true => the charge IS on the invoice, so the override protects a line rather than
   *  suppressing a charge. Drives the wording and which figures are shown. */
  onInvoice: boolean;
  /** The decision was agreed at a figure that has since moved, so approval is being
   *  refused until somebody re-verifies it. Carried here because this strip is where an
   *  override LIVES on the tab — the estimate modal is a thing people open, and a
   *  refusal whose only explanation is behind a button nobody pressed reads as a bug. */
  needsReview: boolean;
  /** What it was agreed at, against `withheldTotal` as the figure now. */
  stampedTotal: number | null;
}

/**
 * Is the document a decision belongs to BILLED? That, not the order, decides whether the
 * decision can still be withdrawn.
 *
 * An order's invoices are billed one at a time, so the order-level `invoiced` flag only
 * ever answers for the main invoice. An override on an OPEN accessorial behind a billed
 * main invoice describes money that can still be billed, so it is still a change of mind
 * with a consequence — and the server agrees: `overrides.is_invoiced` takes the same
 * invoicerecno and the estimate's own contract says a billed document is the one where
 * "nothing on it can be staged or overridden".
 *
 * Falls back to the order-level flag when there is no projection to ask — not loaded yet,
 * or a charge with no invoice row of its own. That is the conservative side: it withholds
 * Remove rather than offering a button the server answers with a 409.
 */
/**
 * Which invoice each charge on the pro belongs to, by line-item type.
 *
 * The callout strips under Quote Charges list charges from the WHOLE order — a chassis
 * split on an accessorial sits in the same strip as a dray correction on the main
 * invoice — but they were opening the override dialog with no document named. The server
 * then falls back to the main invoice (`_override_document`), and since that lock became
 * per document an override on an OPEN accessorial behind a BILLED main invoice was
 * refused: "This invoice has been billed." The charge it was about had not been billed at
 * all. Caught on pro 5037500 (CHASSIS SPLIT on accessorial 2, main invoice billed 07/27).
 *
 * `rejected` is read as well as `lines` because a declined charge never reaches an
 * invoice line, and it is exactly the kind a user reaches for this dialog to record.
 * First non-null wins: a type bills on one document per order, and a later null would
 * otherwise erase a recno already found.
 */
export function overrideDocByItem(
  estimate: {
    lines?: ReadonlyArray<{ item: string; invoiceRecno?: number | null }>;
    rejected?: ReadonlyArray<{ item: string; invoiceRecno?: number | null }>;
  } | null | undefined,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const ln of [...(estimate?.lines ?? []), ...(estimate?.rejected ?? [])]) {
    const k = (ln.item ?? '').trim().toUpperCase();
    if (!k || m.has(k)) continue;
    if (ln.invoiceRecno) m.set(k, ln.invoiceRecno);
  }
  return m;
}

export function isOverrideDocBilled(
  invoiceRecno: number | undefined,
  invoices: ReadonlyArray<InvoiceProjection> | undefined,
  orderInvoiced: boolean,
): boolean {
  const rec = invoiceRecno ?? 0;
  if (!rec) return orderInvoiced;
  const doc = invoices?.find((p) => p.invoiceRecno === rec);
  return doc ? !!doc.invoiced : orderInvoiced;
}

/**
 * The overrides listed under Quote Charges: EVERY override on the pro, billed or not.
 *
 * It used to list only the charges with NO invoice line while the rows were still
 * editable, on the reasoning that an on-invoice override was already visible on its own
 * row and repeating it here would say the same thing twice. That left the strip
 * answering a different question before and after invoicing — and the question people
 * actually bring to it, which charges on this pro carried a human decision, was one
 * nobody could answer until the invoice was closed. It answers it the whole time now.
 *
 * The cost is a billed override being editable from two places: here, and the row's own
 * Edit button. Accepted deliberately — both open the same modal on the same (pro, line
 * item) record, so they cannot disagree, and a list that is always complete beats a
 * rule about where to look.
 *
 * `rejected` contributes nothing after invoicing — the live estimate refuses a billed
 * order and returns a skip with no rejected charges — so on a locked invoice the whole
 * list comes from `overrides`, the record that outlives the projection.
 */
export function selectOverrideCallouts(
  rejected: ReadonlyArray<{
    item: string;
    reason?: string;
    withheldTotal?: number | null;
    overrideNeedsReview?: boolean;
    overrideStampedTotal?: number | null;
  }>,
  overrides: ReadonlyArray<LineItemOverride>,
  invoiceItems: ReadonlyArray<string | null | undefined>,
): OverrideCallout[] {
  const onInvoice = new Set(
    invoiceItems.map((i) => (i ?? '').trim().toUpperCase()).filter(Boolean),
  );
  const key = (s: string | null | undefined) => (s ?? '').trim().toUpperCase();

  const fromEstimate: OverrideCallout[] = rejected
    .filter((r) => !onInvoice.has(key(r.item)))
    .map((r) => ({
      item: r.item,
      reason: r.reason ?? '',
      withheldTotal: r.withheldTotal ?? null,
      // These reach the strip only when the charge has no line, by the filter above,
      // so there is nothing billed to compare against.
      billedTotal: null,
      onInvoice: false,
      needsReview: r.overrideNeedsReview ?? false,
      stampedTotal: r.overrideStampedTotal ?? null,
    }));
  const seen = new Set(fromEstimate.map((r) => key(r.item)));

  const fromOverrides = overrides
    .filter((o) => !seen.has(key(o.liitem)))
    .map((o) => ({
      item: o.liitem,
      // Reason, then the Additional Comments when there are any — the strip has one
      // slot per override and the comments are where the specifics live.
      reason: o.comments ? `${o.reason} — ${o.comments}` : o.reason,
      onInvoice: onInvoice.has(key(o.liitem)),
      // The GROSS — what this charge would have been — not the net.
      //
      // The net (charge minus what the PRO bills for it elsewhere) is the right
      // measure for the REPORT, which totals declined revenue across the book and
      // must not count a charge twice because it moved to an accessorial. It is the
      // wrong measure HERE: beside "CHASSIS SPLIT — not billed", a reader takes the
      // number to mean what CHASSIS SPLIT would have been. Netting produced things
      // like "-10.00" on a charge nobody has a negative opinion about — it was the
      // accessorial billing $10 more than the quote rate, which is a fact about the
      // accessorial, not about this decision.
      //
      // The estimate-sourced rows above already carry the gross in `withheldTotal`,
      // so this keeps the two halves of the list reading in the same units.
      withheldTotal: o.grossAmount,
      // BOTH figures on a billed charge, because an override there is a choice between
      // two numbers and showing one of them hides the decision. On 5067574 the engine
      // wanted the quote's 232.00 and the invoice bills 289.00; either figure alone is
      // a fact, and only the pair is the reason anybody overrode anything.
      billedTotal: onInvoice.has(key(o.liitem)) ? o.billedAmount : null,
      // Only the estimate knows whether a decision has drifted — it is the comparison
      // of the stamp against what the engine would charge NOW, and an override record
      // on its own carries only the stamp. A row that reaches the strip from the
      // override table alone is one the estimate had nothing to say about (an invoiced
      // order, where the engine declines to run at all), so there is no drift to report.
      needsReview: false,
      stampedTotal: null,
    }));

  return [...fromEstimate, ...fromOverrides];
}

export function applyEstimateToRows(
  currentRows: ReadonlyArray<EditingRow>,
  estimate: { lines: ReadonlyArray<EstimatedLine> },
): { rows: EditingRow[]; stagedKeys: Set<string> } {
  let next = [...currentRows];
  const stagedKeys = new Set<string>();

  // WHICH lines stage, and with what figures, is `estimateStagedChanges` — shared with
  // the Accessorial modal so the two editors cannot drift on it. What is left here is
  // only how a change lands on THIS editor's row type.
  for (const change of estimateStagedChanges(estimate.lines, isMainInvoiceLine)) {
    if (change.action === 'remove') {
      next = next.filter((r) => r.recno !== change.existingRecno);
      continue;
    }

    if (change.action === 'update') {
      next = next.map((r) => {
        if (r.recno !== change.existingRecno) return r;
        stagedKeys.add(r._key);
        return {
          ...r,
          qty: change.qty,
          rate: change.rate,
          rateType: change.rateType || r.rateType,
          total: change.total,
          description: change.description,
          glCode: change.glCode || r.glCode,
        };
      });
      continue;
    }

    const _key = nextKey();
    stagedKeys.add(_key);
    next.push({
      recno: undefined,
      _key,
      lineNumber: nextLineNumber(next),
      item: change.item,
      glCode: change.glCode,
      qty: change.qty,
      rate: change.rate,
      rateType: change.rateType,
      description: change.description,
      total: change.total,
    });
  }

  return { rows: next, stagedKeys };
}

/** The change-all fan-out payload for one flagged line, snapshotted when the box is
 *  ticked (and kept fresh on edits). Snapshotting — rather than re-finding the row
 *  after the footer Save — is deliberate: the save re-seeds line items with fresh
 *  recno/_key AND the backend reassigns linumber for new rows, so NO client identity
 *  survives the round-trip. `isNew` (the row had no recno when flagged) tells the
 *  backend to APPEND it to every target; an existing line matches by `linumber`. */
export const rowToLinePayload = (r: EditingRow): Record<string, unknown> => ({
  isNew: r.recno === undefined,
  linumber: r.lineNumber,
  item: r.item,
  // What the targets should still be holding, so the server can tell "the charge I
  // edited" from "a different charge in that slot". Falls back to the current item
  // for a row loaded without one, which is the unedited case.
  originalItem: r.originalItem ?? r.item,
  glCode: r.glCode,
  qty: r.qty,
  rate: r.rate,
  rateType: r.rateType,
  description: r.description,
  total: r.total,
});

// Re-exported for existing importers (InvoicingView.test.tsx); definition lives
// in ./invoiceLineItemShared.
export { computeLineTotal };

/** Convert an InvoiceLineItem array from OrderDetails into EditingRow state. */
function detailsToRows(items: InvoiceLineItem[] | undefined): EditingRow[] {
  return (items ?? []).map((li) => ({
    recno: li.recno,
    _key: nextKey(),
    lineNumber: li.lineNumber,
    item: li.item,
    // Captured at LOAD and never touched by an edit — that is the whole point. A save
    // re-seeds these rows from the server, so it re-baselines to the new saved value.
    originalItem: li.item,
    glCode: li.glCode,
    qty: li.qty,
    rate: li.rate,
    rateType: li.rateType,
    description: li.description,
    total: li.total,
  }));
}

/** V1 parity (lib_editrecord.pl:1446-1457): validate the invoice-hold pair on
 *  save. Returns an error message that should block the save, or null when OK.
 *  Rule order matches V1 — the required-check runs before the cannot-clear
 *  check. */
export function validateInvoiceHold(
  holdReason: string,
  followUp: string,
  priorFollowUp: string,
): string | null {
  if (holdReason.trim() !== '' && followUp.trim() === '') {
    return 'Last Follow Up is required when a Hold Reason is selected. Please update.';
  }
  if (priorFollowUp.trim() !== '' && followUp.trim() === '') {
    return 'Last Follow Up cannot be cleared once it has been set. Please update.';
  }
  return null;
}

// Builds the read-only label shown in the Quote Charges panel for an order that
// carries a legacy v2 quote cross-reference but no v1 quote. There is no live V2
// system to pull charges from, so this is display-only. Revision (0 = none) and
// lane (blank) are appended only when present, e.g. "V2 Quote 3006316-2 — CMA".
export function formatV2QuoteRef(
  v2QuoteNumber: number,
  v2RevisionNumber: number,
  v2Lane: string,
): string {
  let label = `V2 Quote ${v2QuoteNumber}`;
  if (v2RevisionNumber > 0) label += `-${v2RevisionNumber}`;
  const lane = (v2Lane || '').trim();
  if (lane) label += ` — ${lane}`;
  return label;
}

// This tab owns the "apply to all" checkboxes and nothing else about the fan-out. The
// field maps, the ChangeSet builder and the review modal live in ../changeAll/fanout,
// driven by ProDetailContainer: the tab is not mounted when the user saves from another
// one, and its flags are now merged into the single order-level Apply (bugfix #33440).
// Re-exported here for existing importers.
export {
  buildInvoicingChangeSet,
  mergeInvoicingPreviewRows,
  mergeInvoicingApplyResults,
  INVOICE_HEADER_VALUE_KEYS,
} from '../changeAll/fanout';

/** Per-field "apply to all pros" flag for the invoicing header. Renders nothing
 *  when change-all is off. Bound to InvoicingView's LOCAL checkedInv set (kept
 *  separate from the order-field allowlist the context banner tracks).
 *
 *  THREE questions, and they are genuinely different:
 *
 *  REGISTRY — is Change All open for this field at all? Order-arm fields are the
 *  registry's call, exactly as they are for the order tabs' BulkFieldCheckbox. This tab
 *  was built with its own checkbox and never grew that gate, so
 *  `field_definitions.is_change_all` did not govern it: a field the registry had never
 *  opened drew a checkbox anyway. Since the endpoint began enforcing the same registry
 *  that stopped being cosmetic — `/change-all/preview` refuses the whole ChangeSet over
 *  one unflagged order column, so ticking one of these failed the user's ENTIRE fan-out,
 *  including the order-tab fields they had flagged. Invoice-arm fields write cms.invoice,
 *  which the registry does not cover and the endpoint does not enforce — still ungoverned.
 *
 *  PERMISSION — may THIS user edit this field? `editable` comes from the call site and is
 *  the same expression that chooses between the field's input and its ReadValue. It has to
 *  be, because ticking an untouched field fans the ANCHOR's value: a read-only field with
 *  a live tick beside it is not read-only in change-all mode. Since /change-all/apply
 *  enforces only what a single save enforces, this is the gate.
 *
 *  Deliberately NOT also reading the ambient PermissionsContainer level: the fields that
 *  sit in one (COD, Do Not Bill, Quote #) already fold it into the expression passed here,
 *  and for the ones that do not an ambient check would make the TICK stricter than the
 *  field beside it — the same failure inverted, and invisible.
 *
 *  TEMPLATE POLICY — on a template anchor a field the policy locks draws no checkbox.
 *  Read from the context rather than passed in at each of the ~17 call sites. */
const InvBulkCheckbox: React.FC<{
  enabled: boolean;
  field: string;
  label: string;
  checkedInv: ReadonlySet<string>;
  onToggle: (field: string) => void;
  /** May this user edit this field on this record? */
  editable?: boolean;
}> = ({ enabled, field, label, checkedInv, onToggle, editable = true }) => {
  const { isTemplate, fieldAllowed } = useChangeAll();
  const { isFannable } = useChangeAllFields(enabled);
  if (!enabled) return null;
  if (!editable) return null;
  // Grouped fields (the approval stamps, the quote link) are one checkbox over a
  // PAIR, and the registry knows the halves rather than the grouped name — asking
  // it about the name is what left the approval stamps permanently closed.
  if (invoicingFieldArm(field) === 'order'
      && !invoicingFieldViewKeys(field).every(isFannable)) return null;
  if (isTemplate && !fieldAllowed(field)) return null;
  return (
    <input
      type="checkbox"
      aria-label={`Apply ${label} to all`}
      title="Apply this field to all pros"
      checked={checkedInv.has(field)}
      onChange={() => onToggle(field)}
      className="h-3.5 w-3.5 shrink-0 accent-blue-600 cursor-pointer"
    />
  );
};

/** Options for the required-for-invoicing flags + COD. */
const REQUIRE_OPTIONS = ['N', 'Y', 'RESOLVED'] as const;

/** Line-item fields that change what the invoice BILLS — the mirror of
 *  `invoice_lineitem_service.MONEY_COLUMNS`, which is what the backend re-flags off.
 *  `item` is in here because swapping CHASSIS SPLIT for PRE PULL changes the charge
 *  even at the same figures; `glCode` and `description` are not — they reclassify or
 *  annotate a charge without altering it. */
const MONEY_FIELDS: ReadonlySet<string> = new Set([
  'qty', 'rate', 'rateType', 'total', 'item',
]);
const COD_OPTIONS = ['N', 'Y'] as const;
/** Freight Terms dropdown values (V1). */
const FREIGHT_TERMS_OPTIONS = ['', 'COLLECT', 'PREPAID', '3RD PARTY'] as const;
// Main-invoice Bill From values (same set as the accessorial modal). 'DO NOT BILL'
// surfaces the Do-Not-Bill reason field alongside. Editable on a real order behind the
// field's own permission (invoice-bill-from) and on an unapproved template (State 2).
const BILL_FROM_OPTIONS = ['CMS', 'DO NOT BILL'] as const;
/** Credit-reason values — the same V1 list the Accessorial modal uses
 *  (CREDIT_REASONS there). Shown only when the invoice carries a credit. */
const CREDIT_REASON_OPTIONS = [
  '', 'REBILLED: WRONG BILL TO', 'REBILLED: RATE DISCREPANCY',
  'REBILLED: INFORMATION/REF# DISCREPANCY', 'REBILLED: INCORRECT FORMATTING/GROUPING',
  'REBILLED: OTHER', 'NOT REBILLED: ACCOUNTING ERROR', 'NOT REBILLED: CSR ERROR',
  'NOT REBILLED: DISPATCH ERROR', 'NOT REBILLED: SALES ERROR', 'NOT REBILLED: OTHER',
  'WAIVING TO APPEASE CUSTOMER', 'WIRE TRANSFER FEE',
] as const;
/**
 * Keystroke filter for Credit Amt — the port of V1's `cmsadjcheck`
 * (common_html5.js:849), which is bound to this input and to nothing else on the tab.
 *
 * Digits and at most ONE decimal point survive; everything else is dropped, the minus
 * sign included ("You can not enter negative values"). A credit is stored positive and
 * subtracted on the invoice PDF, so a negative here would add money back.
 */
export function creditAmountInput(raw: string): string {
  let seenDot = false;
  let out = '';
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') out += ch;
    else if (ch === '.' && !seenDot) { seenDot = true; out += ch; }
  }
  return out;
}

/** Missing-Invoice reason values — the same V1 list the Accessorial modal uses
 *  (MISSING_INV_REASONS there). Shown only when Missing Inv Approved is 'Y'. */
const MISSING_INV_REASON_OPTIONS = [
  '', 'BILLED ON ACCESSORIAL', 'INCORRECT INVOICE LINE ITEM', 'OTHER',
] as const;
/** Rebill/Credit dropdown values. */
const REBILL_OPTIONS = ['Y', 'N'] as const;
/** Do-Not-Bill Status dropdown values (V1 @donotbill_data). */
const DO_NOT_BILL_OPTIONS = [
  '', 'NOT APPROVED', 'RESEARCHING', 'OUT OF POCKET - COST OF DOING BUSINESS',
  'OUT OF POCKET - OFI', 'BILLED ELSEWHERE', 'DRY RUN', 'CHARITY',
  'CUSTOMER PICKUP', 'INVENTORY ADJUSTMENT', 'OVERFLOW IN RATE', 'OTHER',
] as const;

/**
 * Compact label + <select> whose CELL highlights when the value warrants it —
 * yellow for the required-for-invoicing flags (parity with the Order-Info
 * tank/scale dropdowns) and red for COD='Y'. Fits multi-char values (RESOLVED),
 * unlike the single-letter FlagInput. Read-only renders a value chip.
 */
const InvoiceFlagSelect: React.FC<{
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  readOnly?: boolean;
  highlight?: 'yellow' | 'red';
  labelWidth?: number;
  ariaLabel?: string;
  /** Optional trailing slot — the change-all "apply to all" checkbox. */
  bulk?: React.ReactNode;
}> = ({ label, value, options, onChange, readOnly, highlight, labelWidth = 92, ariaLabel, bulk }) => {
  const isYellow = highlight === 'yellow';
  const isRed = highlight === 'red';
  const wrapHl = isYellow
    ? 'bg-yellow-300 dark:bg-yellow-300'
    : isRed
      ? 'bg-brand-red'
      : '';
  const emphText = isYellow ? 'text-black' : isRed ? 'text-white' : '';
  // Force the stored value into the list when it isn't there (V1
  // smod_forcevalueinarray). The old expression was guarded on `value &&`, so it
  // covered a legacy out-of-list value but NOT a blank — and a blank paired with
  // the `value || 'N'` below displayed as 'N' on a column that held ''.
  const forced = forcedSelectOption(value, options);
  const opts = forced ? [forced.value, ...options] : options;
  return (
    <div className={`flex items-center gap-1 py-0.5 rounded-sm min-h-[26px] px-1 ${wrapHl}`}>
      <span
        className={`text-[0.71875rem] font-medium text-right ${emphText || 'text-slate-600 dark:text-gray-300'}`}
        style={{ width: labelWidth }}
      >
        {label}:
      </span>
      {readOnly ? (
        <span
          aria-label={ariaLabel ?? label}
          className={`text-[0.75rem] font-semibold px-1 inline-flex items-center h-[22px] ${emphText || 'text-slate-800 dark:text-gray-200'}`}
        >
          {value}
        </span>
      ) : (
        <select
          aria-label={ariaLabel ?? label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-700 rounded text-[0.75rem] font-semibold px-1 h-[22px] cursor-pointer text-slate-900 dark:text-gray-100"
        >
          {opts.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      )}
      {bulk}
    </div>
  );
};

const InvoicingView: React.FC<InvoicingViewProps> = ({ details, pronumber, editing, canEditTemplate = true, onSaved, createMode = false }) => {
  const d = details ?? {};
  // Whse-tab warehouse rule (Dawn's ticket 2026-10-14): a GWI-coded line should name a
  // warehouse the pro worked through. `whseCodes` is the server's list (receiving +
  // BL rows); a miss paints the GL Code cell red with the warehouse in the tooltip,
  // on load. The highlight is deliberately BROAD — every item, V1's advisory callout —
  // while useOrderEditing only refuses Pending Approval→RESOLVED on the dray items
  // (WHSE_BLOCKING_ITEMS). Empty list = no MR/BL yet = nothing to compare, nothing flags.
  const whseCodes = d.whseCodes ?? [];
  const whseGlTitle = (code: string | null | undefined): string | undefined =>
    whseGlCodeMatches(code, whseCodes) ? undefined : `Whse tab shows ${whseCodes.join(', ')}`;

  // SWAP the color set, never stack it. Tailwind emits utilities in theme-key order and
  // this project's ramp palette is ALPHABETICAL (src/theme/rampColors.cjs), so every
  // *-red-* rule lands BEFORE *-slate-* / *-white in the stylesheet. Appending
  // `border-red-500 bg-red-50 text-red-700` onto a base that already carries
  // `border-slate-300 bg-white text-slate-900` therefore renders NOTHING in light mode —
  // equal specificity, and the neutral rule comes later, so it wins. (That is how this
  // highlight shipped invisible: the class was on the element, the CSS just lost.) The
  // stock-Tailwind "append an override" idiom does not hold in this repo.
  const GL_SELECT_BASE = 'w-full border rounded px-1 text-[0.75rem] h-6';
  const GL_SELECT_NEUTRAL =
    'bg-white dark:bg-gray-900 border-slate-300 dark:border-gray-600 text-slate-900 dark:text-gray-100';
  const GL_SELECT_FLAGGED =
    'border-red-500 bg-red-50 text-red-700 dark:border-red-500 dark:bg-red-900/30 dark:text-red-300';
  const glSelectClass = (code: string | null | undefined): string =>
    `${GL_SELECT_BASE} ${whseGlTitle(code) ? GL_SELECT_FLAGGED : GL_SELECT_NEUTRAL}`;
  const inv = d.invoiceMain ?? null;

  // Editing requires a numeric order pronumber. Template pronumbers arrive as
  // "T…" strings and stay read-only (line items render from the details payload).
  // Create mode has no pronumber at all yet, but everything here is staged into
  // the editing hook (header via editedOrderFields, lines via the draft) and
  // committed by the single create POST — so it edits like a real order. Every
  // fetch/modal that needs a persisted pro is separately guarded on
  // `numericPronumber`, which stays null.
  const numericPronumber = typeof pronumber === 'number' ? pronumber : null;
  const editable = numericPronumber != null || createMode;
  // Invoice Hold (reason + last follow-up) is LC-gated on real orders via the
  // shared order-lc-fields permission — same container as the Order Info LC
  // fields. Templates stay read-only (editable is false for T-pronumbers).
  const canEditOrderLcFields = useCanEditOrderLcFields();
  const holdEditable = editable && canEditOrderLcFields;
  // Bill From has its OWN permission rather than riding holdEditable, which is what it
  // used to do. Standalone, NOT crossed with the LC gate: the invoicing desk is View on
  // order-lc-fields and must still be able to change it, and levels resolve per
  // component independently. See useCanEditBillFrom for the split this restores.
  const billFromEditable = editable && useCanEditBillFrom();
  // COD (Field 3) has its own permission — read-only until granted (level >= 2).
  const canEditCod = editable && useResolvedAccessLevel(COD_PERMISSION_ID, false, true) >= 2;
  // Do Not Bill Reason — manager-only gate, read-only until granted. Resolved
  // unconditionally (a hook may not sit behind `editable &&`) and crossed with
  // `editable` after, so a read-only mount never reports the field as editable.
  const doNotBillReasonAccess = useResolvedAccessLevel(
    DO_NOT_BILL_REASON_PERMISSION_ID, false, true,
  );
  const canEditDoNotBillReason = editable && doNotBillReasonAccess >= ACCESS_LEVEL_EDIT;
  // Quote # has its own permission with the Exempt scheme (see the export's
  // doc). Un-granted resolves to Exempt so the field stays editable for groups
  // nobody has configured; the order-level LC gate (holdEditable) still applies
  // on top, keeping plain-Dispatch (order-lc-fields View) users read-only.
  const quoteNumberAccess = useResolvedAccessLevel(
    QUOTE_NUMBER_PERMISSION_ID,
    false,
    false,
    ACCESS_LEVEL_EXEMPT,
  );
  const canEditQuoteNumber = holdEditable && quoteNumberAccess >= ACCESS_LEVEL_EDIT;

  // V1 not-invoiced gate: once the invoice has an invoicedate, line items lock
  // (read-only). Header fields (hold, comments, credit, margin) stay editable —
  // CSRs still work those post-invoice.
  const invoiced = !!(inv?.invoiceDate && String(inv.invoiceDate).trim() !== '');
  // Customer-facing invoice number. Blank until the invoice is invoiced (see the
  // render site above the line items).
  const invoiceNumber = String(inv?.invoiceNumber ?? '').trim();
  // The raised invoice this pro's main charge was billed under — what Invoice Options
  // acts on. 0 on a draft and on a template, and the button hides itself on 0.
  const mainInvHistoryRecno = Number(inv?.invHistoryRecno ?? 0);
  // The config layer (order_field_display_rules) is now the authority on WHICH invoice
  // fields the invoiced state locks — v1's orderinvoiced ACL, seeded by migrations
  // 329/447 — and it is also where Admin Override (#33501) releases them. `roField`
  // reports whether a field is locked right now; with override on, the rules that opted
  // in drop out of the readonly set and it reports false.
  //
  // `invoice_lineitem` is not a column: it is v1's subtable widget, and its readonly rule
  // is what removes add/delete of lines. Kept distinct from the per-cell li* rules so the
  // grid can ask one question.
  //
  // Templates are exempt — templateFieldPolicy is the sole authority there, and the
  // seeded rules are cms-system only.
  const disp = useOrderFieldDisplayContext();
  const roField = (key: string): boolean => !isTemplate && disp.isFieldReadonly(key);
  // Red-then-black highlight for the main invoice's Invoice/Sage-Date cell.
  const invExportHl = invoiceExportHighlight(inv?.invoiceDate, inv?.masExportDate);
  // Line-item editability (template flags are wired just below, after the template
  // gate is derived). `lineItemsEditable` = FULL edit (item/qty/rate/type/desc +
  // add/delete) → the input-row layout; `glOnlyLineEdit` = an approved template
  // rendered as a completed-invoice read-out with ONLY the GL Code dropdown live.
  // For a real order these resolve to the classic `editable && !invoiced`.

  // ── template approve / unapprove ─────────────────────────────────────────────
  // The template "approved" switch (order-level approvedby/approveddatetime,
  // surfaced as invoiceApprovedBy/Datetime). It's the one editable control on an
  // otherwise read-only template Invoicing tab. Toggling stamps/clears the pair
  // as a PENDING edit (committed by the footer Save); the field-lock gate keys off
  // the SAVED value, so nothing opens until the (un)approval is saved.
  const isTemplate = isTemplatePronumber(pronumber);
  // Template approval state drives per-field editability via the shared policy
  // (templateFieldPolicy). State 3 = always open; State 2 = open only while
  // unapproved; anything else = locked.
  const isApproved = isTemplateApproved(d);
  const canPersistTemplate = isTemplate && canEditTemplate && !!editing?.updateOrderField;
  // "This tab can persist edits": a real order via the numeric-pronumber `editable`,
  // or a template via the template-edit gate. Used for the shared line-item draft
  // plumbing + lookups (which were numeric-only before templates were writable).
  const invoiceEditable = editable || canPersistTemplate;
  // Per-field template gate (policy + approval). Returns false for a real order.
  const tplEdit = (fieldKey: string): boolean =>
    canPersistTemplate && templateFieldEditable(fieldKey, { isApproved });
  // Field editability: the template policy on a template, else the caller's order gate.
  // On a real order the caller's gate is crossed with the config layer, so a field v1's
  // orderinvoiced ACL locks (billfromsystem, invoicecomments) goes read-only once the
  // order is invoiced — and opens again under Admin Override, since both rules are
  // seeded overridable. Before #33501 these stayed editable post-invoice, which
  // contradicted the ACL.
  const fieldEditable = (fieldKey: string, orderGate: boolean): boolean =>
    isTemplate ? tplEdit(fieldKey) : (orderGate && !roField(fieldKey));

  // Line-item editability (see the note above the `invoiced` gate). A template opens
  // ALL line-item fields + add/delete only while UNAPPROVED; once approved just
  // `glCode` stays editable on existing lines (no add/delete). A real order keeps the
  // classic not-invoiced gate.
  // `invoiced` stays the floor rather than deferring to the config layer: the rules fetch
  // falls back to an empty list on error, so a lock that DEPENDED on config would open an
  // invoiced invoice on a network blip. Override only ever RELEASES it, and only when the
  // `invoice_lineitem` rule opted in — no rules loaded, no release.
  const lineItemsEditable = isTemplate
    ? (canPersistTemplate && !isApproved)
    : (editable && (!invoiced || disp.isFieldOverridden('invoice_lineitem')));
  // Approved template: the line grid renders as a completed-invoice read-out
  // (plain text) with ONLY the GL Code cell kept as a dropdown — no maybe-disabled
  // inputs. `lineItemsEditable` (false here) drives the full input-row + add/delete.
  const glOnlyLineEdit = isTemplate && canPersistTemplate && isApproved;
  /**
   * GL CODE STAYS EDITABLE PAST THE INVOICED LOCK, until the invoice reaches Sage.
   *
   * Asked for by the invoicing desk (Lee, 2026-10-21). Reclassifying a line changes no
   * figure the customer sees — only which account the revenue lands in — so it is the one
   * line-item column worth opening after an invoice has gone out. `masexportdate` is the
   * boundary: past it the figure is posted to a receivable and a reclassify here would
   * silently disagree with what Sage holds. (The column is a DATE named `masexportdate`,
   * not `masexportdatetime`.)
   *
   * The INVERSE of the Credit Amt rule above, which unlocks once the export exists —
   * same column, opposite side, and both are V1's own boundaries.
   *
   * Gated by a `PermissionsContainer` rather than this hook alone so the control carries
   * the configuration handle in permissions mode: the grant starts with the invoice
   * managers and an admin can widen it without a deploy. The container is the thing that
   * renders the cog; `useResolvedAccessLevel` on its own would gate correctly and give
   * admins nothing to click.
   *
   * VIEW-ONLY WHEN UN-GRANTED, NOT HIDDEN (Lee, 2026-10-21). Everyone still has to be
   * able to READ the GL code on an invoiced line — it is on the screen today and the
   * warehouse-code red flag is rendered from it. `defaultHidden` would resolve an
   * un-granted user to level 0, and a PermissionsContainer at 0 renders NOTHING, so the
   * cell came up blank for them: the read-only `<span>` below is in the other arm of
   * the ternary and never ran. `defaultDisabled` lands them on level 1 instead — the
   * container renders, and the level decides dropdown vs. plain text.
   */
  const sageExported = !!(inv?.masExportDate || '').trim();
  const glCodeAfterInvoice = !lineItemsEditable && !isTemplate && !sageExported;
  //: Only an EDIT grant turns the after-invoice GL cell into a dropdown. Resolved once,
  //: at component level — the cell is rendered per row and a hook cannot live in that
  //: loop. An approved TEMPLATE keeps its own authority (`glOnlyLineEdit`) and asks
  //: nothing of this component, exactly as it did before the after-invoice case existed.
  const glCodeAccess = useResolvedAccessLevel(GL_CODE_COMPONENT_ID, false, true);
  const canEditGlAfterInvoice = glCodeAfterInvoice && glCodeAccess >= ACCESS_LEVEL_EDIT;
  const perms = useTryPermissions();
  /**
   * Who may RECORD a not-billing decision. Overriding declines revenue — $1,035,927.31
   * across 5,895 of them on prod as of 2026-10-20 — and it is an LC's call (Lee,
   * 2026-10-20): all 41 people who have ever written one are Logistic Coordinators.
   *
   * SEPARATE FROM `lineItemsEditable`, which is what used to show these controls. That
   * asks "may you edit invoice lines", a different question with a wider audience: six
   * invoice clerks write line items daily and would otherwise see an "Override" button
   * that 403s against the server gate (migration 536). None of them has ever recorded an
   * override, so this hides a control they do not use rather than removing one they do.
   *
   * READING is deliberately NOT gated. The override strip, the Overridden badges and the
   * reasons stay visible to everyone who can see the pro — same split as the server,
   * where the LIST endpoint is granted to GWI360-Everyone and only the write verbs are
   * restricted. Seeing that a charge was declined is not the same as declining one.
   *
   * defaultHidden: no grant => 0 => the controls are gone. Migration 533 must land WITH
   * the deploy or the LCs lose them; it ships alongside 532, which the server gate needs
   * for the same reason, so both are one pre-deploy step.
   */
  const canOverride = useResolvedAccessLevel('invoice-override', true) >= 2;
  // v1_username is the CMS approver name; it can be unmapped for an Azure
  // account, so fall back to the email so the stamp is never empty (an empty
  // approvedby reads as "not approved" and breaks the toggle).
  const currentUser =
    perms?.permissions?.v1_username || perms?.permissions?.user_email || '';
  // Field 8 — the approve checkbox is accessible on real orders too, not just
  // templates (canEditTemplate). The stamp rides orderData (invoiceApprovedBy)
  // either way so sr_approved fires.
  //
  // The ORDER arm has its own component permission rather than riding `holdEditable`
  // (order-lc-fields), which is what it used to do. Approving an invoice and editing
  // Port of Origin / Discharge / DC / Dept are different jobs: invoice clerks do the
  // first and not the second. See useCanApproveInvoice for the V1 ACL this restores.
  // Still crossed with `editable` — a non-persisting mount (no numeric pro, not
  // create mode) stays read-only no matter who is looking.
  const canApproveInvoice = useCanApproveInvoice();
  const canToggleApproval =
    (isTemplate ? canEditTemplate : editable && canApproveInvoice) &&
    !!editing?.updateOrderField;
  const savedApprovedBy = String(d.invoiceApprovedBy ?? '').trim();
  const effApprovedBy = String(
    editing?.editedOrderFields?.invoiceApprovedBy ?? d.invoiceApprovedBy ?? '',
  ).trim();
  const effApprovedDatetime = String(
    editing?.editedOrderFields?.invoiceApprovedDatetime ?? d.invoiceApprovedDatetime ?? '',
  ).trim();
  // "Approved" for the control = EITHER stamp present, so a prior approval that
  // landed with an empty username can still be unchecked/cleared.
  const approvedEffective = effApprovedBy !== '' || effApprovedDatetime !== '';
  const approvalPending =
    editing?.editedOrderFields?.invoiceApprovedBy !== undefined &&
    effApprovedBy !== savedApprovedBy;
  const toggleApproval = () => {
    if (!editing?.updateOrderField) return;
    if (approvedEffective) {
      editing.updateOrderField('invoiceApprovedBy', '');
      editing.updateOrderField('invoiceApprovedDatetime', '');
    } else {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
      editing.updateOrderField('invoiceApprovedBy', currentUser);
      editing.updateOrderField('invoiceApprovedDatetime', stamp);
    }
  };

  // ── header edits ────────────────────────────────────────────────────────────
  // All header fields flow through the order-level editing hook
  // (editedOrderFields) so they persist across tab switches and commit on the
  // footer Save (order-invoice-tab batch). `hv` reads the effective value
  // (edited wins, else the saved source); `uh` stages an edit. `savedHeaderValue`
  // maps each key to its saved source — invoice-table fields on `inv`, ordersall
  // fields on the top-level details object.
  const ef = editing?.editedOrderFields ?? {};
  const upd = editing?.updateOrderField;
  const str = (v: unknown): string => (v == null ? '' : String(v));
  const savedHeaderValue = (key: string): unknown => {
    switch (key) {
      case 'invoiceComments': return inv?.invoiceComments ?? '';
      case 'doNotBillReason': return inv?.doNotBillReason ?? '';
      // Invoice-table column, NOT an ordersall one — without this case it fell
      // through to `d.billFromSystem` (undefined) and the dropdown always seeded
      // 'CMS' regardless of what was stored.
      case 'billFromSystem': return inv?.billFromSystem ?? '';
      // Seed the box the way it reads: blank at zero (V1's "no credit" sentinel,
      // and what ReadValue renders), two decimals otherwise — `String(2357.2)` would
      // otherwise put "2357.2" in a money field.
      case 'creditAmount':
        return Number(inv?.creditAmount ?? 0) !== 0 ? Number(inv?.creditAmount).toFixed(2) : '';
      case 'creditReason': return inv?.creditReason ?? '';
      case 'billToRefNo': return inv?.billToRefNo ?? '';
      case 'estInvoiceEmail': return inv?.estInvoiceEmail ?? '';
      // Linked quote — numbers, 0 = unlinked (V1's "no quote" sentinel).
      case 'quoteNumber': return inv?.quoteNumber ?? 0;
      case 'revisionNumber': return inv?.revisionNumber ?? 0;
      case 'requireEdi':
      case 'requireMrpl':
      case 'requirePod':
      case 'requireBol':
      case 'requireInInterchange':
      case 'requireOutInterchange':
      case 'requireChassisInEir':
      case 'requireChassisOutEir':
      case 'requireRevisedDo':
      case 'pendingApproval':
        return (inv as unknown as Record<string, unknown> | null)?.[key] ?? 'N';
      default:
        // ordersall header fields (rebill, rateComments, marginApprovedBy,
        // invoiceHoldReason/followup, cod, freightTerms, requirePerDiemApproval).
        return (d as Record<string, unknown>)[key] ?? '';
    }
  };
  const hv = (key: string): unknown => (ef[key] !== undefined ? ef[key] : savedHeaderValue(key));
  const uh = (key: string, value: unknown): void => upd?.(key, value);

  // Field 1 — Margin approval checkbox: checking client-stamps the current user +
  // datetime into marginapprovedby/marginapproveddatetime (same pattern as the
  // template approve toggle, so it round-trips through orderData in both save
  // modes); unchecking clears both. The by/date stay read-only displays.
  const marginApproved = str(hv('marginApprovedBy')) !== '';
  const toggleMarginApproval = () => {
    if (marginApproved) {
      uh('marginApprovedBy', '');
      uh('marginApprovedDatetime', '');
    } else {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = `${pad(now.getMonth() + 1)}/${pad(now.getDate())}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
      uh('marginApprovedBy', currentUser);
      uh('marginApprovedDatetime', stamp);
    }
  };

  // Field 4 — estimated-invoice email format check (blank is valid).
  const estEmailInvalid = invoiceEditable && !isValidEmailList(str(hv('estInvoiceEmail')));

  // Field 5 — Bill To Contact lookup (contactinfo where tablename='billtos',
  // tablerecno=order.billTo). Mirrors the Addl Info broker-contact pattern:
  // dropdown of contacts + phone/email under it. Only the recno persists (via
  // orderData → billtocontactrecno); the name/phone/email are read-derived.
  const billToRecno = Number(d.billTo) || 0;
  const [billToContacts, setBillToContacts] = useState<Contact[]>([]);
  useEffect(() => {
    if (!invoiceEditable || !billToRecno) { setBillToContacts([]); return; }
    let cancelled = false;
    listContacts('billtos', billToRecno)
      .then((r) => { if (!cancelled) setBillToContacts(r); })
      .catch(() => { if (!cancelled) setBillToContacts([]); });
    return () => { cancelled = true; };
  }, [invoiceEditable, billToRecno]);
  const effBillToContactRecno = Number(hv('billToContactRecno')) || 0;
  const selectedBillToContact = billToContacts.find((c) => c.recno === effBillToContactRecno);
  const billToContactPhone = selectedBillToContact?.contactphone ?? d.billToContactPhone ?? '';
  const billToContactEmail = selectedBillToContact?.contactemail ?? d.billToContactEmail ?? '';

  // ── Change All (Phase 2) ──────────────────────────────────────────────────
  // Outside a ChangeAllProvider this is a disabled no-op. The invoicing tab has
  // its own Save, so it keeps its OWN checked-set (NOT the context allowlist the
  // order banner tracks) and opens the review modal itself after a successful save.
  //
  // Both flag sets are held by the PROVIDER, not by this component. This tab unmounts
  // on every tab switch, and as local state the flags died with it: tick "apply to all",
  // step over to Tracing and back, Save — and the fan-out never ran, with no modal and
  // no error to say so (bugfix #33440). The provider is scoped to one pro-drawer session,
  // so a different pro still starts clean.
  const changeAll = useChangeAll();
  // Change-all mode alone. Whether any GIVEN tick is drawn is the field's own call —
  // each InvBulkCheckbox takes the same `editable` expression that chooses between its
  // input and its ReadValue, so the tab can never offer to fan a field the user cannot
  // edit here. (Server-side, /change-all/apply enforces what a single save enforces,
  // which for these columns is nothing — this is the gate.)
  const fieldChangeAll = changeAll.enabled;
  const checkedInv = changeAll.checkedInvoiceFields;
  const setCheckedInv = changeAll.setCheckedInvoiceFields;
  const toggleInv = useCallback((field: string) => {
    setCheckedInv((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  }, [setCheckedInv]);
  // Line-item Change All: flagged lines keyed by the row's local _key, VALUE = the full
  // fan-out payload (rowToLinePayload) snapshotted at flag time. We store the payload
  // rather than re-find the row after Save because the save re-seeds line items with new
  // recno/_key and the backend reassigns linumber — no client identity survives, so a
  // re-find would silently drop a newly-added line. A [rows] effect below keeps a flagged
  // line's payload in sync with later edits. Fanned out via the shared `review` above.
  // Line ITEMS are not fanned out from a template. They are not fields — they go
  // through their own endpoint (invoice_lineitem_service), which is order-only — so the
  // whole "All" column is withheld rather than left offering something the apply cannot
  // carry. The header cell and the colSpan below follow the same flag or the table
  // would be one column wider than its rows.
  const lineChangeAll = fieldChangeAll && !isTemplate;
  const checkedLines = changeAll.checkedInvoiceLines;
  const setCheckedLines = changeAll.setCheckedInvoiceLines;
  const toggleLine = useCallback((row: EditingRow) => {
    setCheckedLines((prev) => {
      const next = new Map(prev);
      if (next.has(row._key)) next.delete(row._key);
      else next.set(row._key, rowToLinePayload(row));
      return next;
    });
  }, [setCheckedLines]);

  // Re-seed the rebill/credit count when details change (PRO switch / refresh)
  // so the badge stays current. Header field values need no sync — they read
  // live from editedOrderFields via hv(). (The accessorial inline list re-seeds
  // via its own [details] effect.)
  useEffect(() => {
    setRebillCount(inv?.rebillCount ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  // Hold-reason dropdown options — sourced from cms.invoice_holdreasons (maintained
  // via the Invoice Hold Reasons data-management page) instead of a hardcoded list.
  // Fetched once and session-cached in orderLookups.service; on failure we leave the
  // list empty and still surface the currently-saved reason below.
  const [holdReasons, setHoldReasons] = useState<string[]>([]);
  useEffect(() => {
    // Only the editable dropdown needs the option list; the read-only view shows the
    // raw stored string. Gating avoids a needless fetch (and state update) on the many
    // read-only mounts (dashboard drawers, templates).
    if (!editable) return;
    let alive = true;
    // Promise.resolve() wrapper so a synchronous throw is contained too.
    Promise.resolve()
      .then(() => getInvoiceHoldReasons())
      .then((r) => { if (alive) setHoldReasons(r); })
      .catch(() => { /* leave empty; the current value is still shown as an option */ });
    return () => { alive = false; };
  }, [editable]);
  // Ensure the order's current reason is always selectable even if it's not in the
  // active list (a legacy/renamed/soft-deleted value) — so opening the dropdown never
  // silently drops it.
  const currentHoldReason = str(hv('invoiceHoldReason'));
  const holdReasonOptions =
    currentHoldReason && !holdReasons.includes(currentHoldReason)
      ? [currentHoldReason, ...holdReasons]
      : holdReasons;
  // Do-Not-Bill Status — hardcoded list (V1 @donotbill_data). Keep the current saved
  // value selectable even if it's a legacy value not in the list (same guard as above).
  const currentDoNotBill = str(hv('doNotBillReason'));
  const doNotBillOptions: readonly string[] =
    currentDoNotBill && !(DO_NOT_BILL_OPTIONS as readonly string[]).includes(currentDoNotBill)
      ? [currentDoNotBill, ...DO_NOT_BILL_OPTIONS]
      : DO_NOT_BILL_OPTIONS;

  // Bill From (CMS / DO NOT BILL) drives whether the Do-Not-Bill reason renders
  // at all — same pairing the Accessorial modal uses (showDoNotBill). Legacy
  // values live in prod (a handful of 'MAS'), so keep the stored value selectable
  // rather than silently rewriting it on the next save.
  //
  // A BLANK gets its own leading option instead of borrowing 'CMS' (V1
  // smod_forcevalueinarray, select_html5.pl:514, which prepends the out-of-list
  // value for exactly this reason). Rendering `currentBillFrom || 'CMS'` made the
  // field look already-saved: re-picking the option a select is ALREADY showing
  // fires no change event, so nothing staged into editedOrderFields and — since
  // the save patch carries only staged keys — the blank survived every save. Every
  // user who opened one of these read 'CMS' off a column that said ''. Creates no
  // longer produce blanks (create_defaults.INVOICE_CREATE_FORM_DEFAULTS), so this
  // is for the pre-fix rows: show the gap, and let one pick close it.
  const currentBillFrom = str(hv('billFromSystem'));
  const forcedBillFrom = forcedSelectOption(currentBillFrom, BILL_FROM_OPTIONS);
  const billFromOptions: readonly string[] =
    forcedBillFrom ? [forcedBillFrom.value, ...BILL_FROM_OPTIONS] : BILL_FROM_OPTIONS;
  const isDoNotBill = currentBillFrom.trim().toUpperCase() === 'DO NOT BILL';
  // Leaving DO NOT BILL clears the reason with it (Accessorial-modal parity:
  // `doNotBillReason: showDoNotBill ? doNotBillReason : ''`), so a CMS invoice
  // never keeps a stale not-billed reason the hidden row can no longer show.
  const setBillFromSystem = (value: string): void => {
    uh('billFromSystem', value);
    if (value.trim().toUpperCase() !== 'DO NOT BILL') {
      if (currentDoNotBill !== '') uh('doNotBillReason', '');
      // Drop the change-all flag with the row. Leaving it checked would fan a
      // field the user can no longer see (or uncheck) out to every target pro.
      setCheckedInv((prev) => {
        if (!prev.has('doNotBillReason')) return prev;
        const next = new Set(prev);
        next.delete('doNotBillReason');
        return next;
      });
    }
  };

  // Credit Reason — editable whenever the invoice carries a credit, mirroring the
  // Accessorial modal's `showCreditReason` pairing (a reason with no credit is
  // meaningless, and prod has zero main-invoice rows with an amount but no reason).
  const currentCreditReason = str(hv('creditReason'));
  const creditReasonOptions: readonly string[] =
    currentCreditReason && !(CREDIT_REASON_OPTIONS as readonly string[]).includes(currentCreditReason)
      ? [currentCreditReason, ...CREDIT_REASON_OPTIONS]
      : CREDIT_REASON_OPTIONS;

  // ── Credit Amt ──────────────────────────────────────────────────────────────
  // V1 unlocks this field only once the invoice has been exported to Sage:
  // `if($invdata->{'masexportdate'} eq "") { $acl_ro=$readonly; $acl_attr="ro"; }`
  // (lib_editrecord.pl:8647). Before the export there is no posted receivable to
  // credit against; after it, crediting the exported invoice IS the workflow —
  // both invoices on pro 5062303 carry a credit raised exactly that way.
  const creditAmountText = str(hv('creditAmount'));
  const creditExported = !!(inv?.masExportDate || '').trim();
  const creditAmtEditable = fieldEditable('creditAmount', editable) && creditExported;
  // Follows the EDITED value, so typing an amount reveals Credit Reason and clearing
  // it hides the field again — V1's `cmscheckcredit` blur handler (cms.js:4866).
  const hasCredit = Number(creditAmountText || 0) !== 0;

  // Missing Inv Approved — Y/N derived from the approver stamp, exactly as V1
  // ($miapproved = missinginvoiceapprovedby ? 'Y' : 'N') and the Accessorial
  // modal do, so both surfaces agree on the same order-level columns. Y stamps
  // the current user (keeping an existing approver, per
  // accessorial_service.apply_missing_inv_approval's `prior_by or userid`); N
  // clears the approver AND the reason. missinginvoiceapproveddatetime is
  // deliberately untouched — V1 never set it (0 rows in prod).
  const missingInvApproved = str(hv('missingInvoiceApprovedBy')) !== '' ? 'Y' : 'N';
  const currentMissingInvReason = str(hv('missingInvoiceApprovedReason'));
  const missingInvReasonOptions: readonly string[] =
    currentMissingInvReason
      && !(MISSING_INV_REASON_OPTIONS as readonly string[]).includes(currentMissingInvReason)
      ? [currentMissingInvReason, ...MISSING_INV_REASON_OPTIONS]
      : MISSING_INV_REASON_OPTIONS;
  const setMissingInvApproved = (value: string): void => {
    if (value === 'Y') {
      if (missingInvApproved !== 'Y') uh('missingInvoiceApprovedBy', currentUser);
    } else {
      uh('missingInvoiceApprovedBy', '');
      uh('missingInvoiceApprovedReason', '');
    }
  };

  // Invoice line-item types (cms.invoice_litypes) — the canonical "Item" list for
  // the typeahead, plus the quote-charge → litype mapping used by Add to Invoice.
  // Fetched once (editable mode only), session-cached in orderLookups.service.
  const [liTypes, setLiTypes] = useState<InvoiceLiType[]>([]);
  useEffect(() => {
    if (!invoiceEditable) return;
    let alive = true;
    Promise.resolve()
      .then(() => getInvoiceLiTypes())
      .then((r) => { if (alive) setLiTypes(r); })
      .catch(() => { /* leave empty; the Item field still shows the stored value */ });
    return () => { alive = false; };
  }, [invoiceEditable]);
  const liTypeOptions = useMemo<TypeAheadOption[]>(
    () =>
      liTypes.map((t) => ({
        value: t.name, // stored verbatim in invoice_lineitem.liitem
        label: t.name,
        description: t.displayName && t.displayName !== t.name ? t.displayName : t.glType,
        data: t,
      })),
    [liTypes],
  );
  // additionalcharges.recno -> litype, so adding a quote charge resolves the right item.
  // Many charges can bill under one litype (additionalChargeRecnos, the Quote Charge
  // Mapping junction); the legacy single-slot recno covers backends that predate it.
  // Two passes so a junction mapping beats a STALE legacy slot on a different litype
  // (the server resolves junction-wins per charge, but the old column isn't cleared).
  const liTypeByAddlCharge = useMemo(() => {
    const m = new Map<number, InvoiceLiType>();
    for (const t of liTypes) if (t.additionalChargeRecno) m.set(t.additionalChargeRecno, t);
    for (const t of liTypes) for (const recno of t.additionalChargeRecnos ?? []) m.set(recno, t);
    return m;
  }, [liTypes]);
  // name -> litype. The base-pricing charges have no additionalcharge recno to key on
  // (see BASE_DRAY_ITEM), so their GL code is resolved from the catalogue by name.
  const liTypeByName = useMemo(() => {
    const m = new Map<string, InvoiceLiType>();
    for (const t of liTypes) m.set((t.name ?? '').trim().toUpperCase(), t);
    return m;
  }, [liTypes]);

  // ── line items edit state ───────────────────────────────────────────────────
  // The Invoicing tab is only mounted while active, so its line-item draft would
  // be lost on a tab switch. To fold line items into the order-level Save AND
  // keep them across tab switches, the DRAFT lives in the editing hook: on mount
  // we seed `rows` from the hook draft (if any) else the server line items, and
  // a mirror effect below pushes the current rows + dirty flag back up on every
  // change. The hook's save() commits the draft via the batch endpoint.
  const serverRows = useMemo(
    () => detailsToRows(d.invoiceLineItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [details],
  );
  // Reconstruct EditingRows from a persisted batch draft (fresh _keys).
  //
  // `originalItem` is re-derived from the SERVER rows by recno rather than read off
  // the draft, because the draft holds what is on screen — which, for the edit this
  // matters to, is the new type. Retyping a charge and then switching tabs would
  // otherwise re-baseline the row to its own edit and put the fan-out back to
  // matching on the new type, which is the bug this exists to prevent. A row with no
  // recno was never saved, so it has no baseline and needs none.
  const draftToRows = (items: InvoiceLineItemBatchItem[]): EditingRow[] => {
    const savedItemByRecno = new Map<number, string>(
      (d.invoiceLineItems ?? [])
        .filter((li) => li.recno !== undefined)
        .map((li) => [li.recno as number, li.item]),
    );
    return items.map((li) => ({
      recno: li.recno ?? undefined,
      _key: nextKey(),
      lineNumber: li.lineNumber,
      item: li.item,
      originalItem: li.recno != null ? savedItemByRecno.get(li.recno) : undefined,
      glCode: li.glCode,
      qty: li.qty,
      rate: li.rate,
      rateType: li.rateType,
      description: li.description,
      total: li.total,
    }));
  };
  const [rows, setRows] = useState<EditingRow[]>(() =>
    editing?.invoiceLineItemsDraft
      ? draftToRows(editing.invoiceLineItemsDraft)
      : serverRows,
  );

  // After a save/refresh (details → serverRows changes) sync the editor to the
  // new server truth. Skip the initial run so a mount-time draft (edits made
  // before a tab switch) isn't clobbered by the server rows.
  //
  // CRITICAL: do NOT re-seed while the user has an in-progress line-item draft.
  // A details refresh mid-edit — e.g. editing.refresh() fired by a sibling save,
  // per-diem-note save, or trace action — otherwise wipes the unsaved line edits,
  // which then reports lineItemsDirty=false and drops invliData from the save (so
  // the edit AND its invoice_lineitem history are lost). The hook holds the
  // durable draft signal: a non-null invoiceLineItemsDraft means unsaved edits, so
  // we hold. It's cleared to null on a committed save / PRO change, at which point
  // this re-seed runs and picks up the fresh server rows (with their real recnos).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (editing?.invoiceLineItemsDraft != null) return; // unsaved draft — don't clobber
    setRows(serverRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverRows]);

  // Keep each flagged line's fan-out payload in sync with edits to that row. Only
  // refreshes rows still present by _key — so the post-save re-seed (which swaps in
  // fresh _keys) leaves the pre-save payloads intact for the fan-out, never wiping them.
  useEffect(() => {
    setCheckedLines((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const r of rows) {
        if (next.has(r._key)) {
          next.set(r._key, rowToLinePayload(r));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Re-key surviving line-item flags onto the freshly seeded rows, ONCE per mount.
  // `_key` is a per-mount counter and every re-seed mints new ones, so a flag that
  // outlived a tab switch (they live in the provider now) names a row that no longer
  // exists: its checkbox would read unticked while the flag still fanned out on the next
  // Save — pending work with nothing on screen to say so. The payload carries `linumber`,
  // which a re-seed preserves, so it identifies the row; a flag whose line is gone is
  // dropped rather than fanned blind.
  useEffect(() => {
    setCheckedLines((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map<string, Record<string, unknown>>();
      for (const [key, payload] of prev) {
        if (rows.some((r) => r._key === key)) {
          next.set(key, payload);
          continue;
        }
        changed = true;
        const match = rows.find((r) => r.lineNumber === Number(payload.linumber));
        if (match && !next.has(match._key)) next.set(match._key, rowToLinePayload(match));
      }
      return changed ? next : prev;
    });
    // Mount only: within a mount the [rows] effect above keeps the payloads current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowsSignature = (rs: ReadonlyArray<EditingRow>): string =>
    JSON.stringify(
      rs.map((r) => [
        r.recno ?? null, r.lineNumber, r.item, r.glCode,
        r.qty, r.rate, r.rateType, r.description, r.total,
      ]),
    );
  const lineItemsDirty = invoiceEditable && rowsSignature(rows) !== rowsSignature(serverRows);

  const toBatchItem = (r: EditingRow): InvoiceLineItemBatchItem => ({
    recno: r.recno ?? null,
    lineNumber: r.lineNumber,
    item: r.item,
    glCode: r.glCode,
    qty: r.qty,
    rate: r.rate,
    rateType: r.rateType,
    description: r.description,
    total: r.total,
  });

  // Mirror the current line-item draft up into the editing hook so it survives a
  // tab switch and commits on the footer Save. Editable orders AND editable
  // templates participate; read-only mounts skip it.
  useEffect(() => {
    if (!invoiceEditable || !editing?.setInvoiceLineItemsDraft) return;
    editing.setInvoiceLineItemsDraft(rows.map(toBatchItem), lineItemsDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, serverRows, invoiceEditable]);

  // Hard-block the footer Save while any MAIN-invoice line item carries an Item
  // that isn't a real invoice_litypes entry (renders orange via the Item
  // TypeAhead's flagInvalidValue). "Add to Invoice" on an unmapped quote charge
  // can copy such a bogus item in — staging it is fine (the user gets to fix it),
  // but a bad value must never be persisted. Scope, per the requirement: a real
  // order's main invoice ONLY while it's still un-invoiced (invoice date blank);
  // once invoiced the values are frozen and a later litype change shouldn't
  // retroactively block. Predicate mirrors the TypeAhead's exactly (a non-empty
  // Item absent from the loaded option list) so the block lines up 1:1 with the
  // orange cells; the options.length guard avoids flagging while litypes load.
  const liTypeValueSet = useMemo(
    () => new Set(liTypeOptions.map((o) => o.value)),
    [liTypeOptions],
  );
  const invalidMainInvoiceItems = useMemo(
    () =>
      !isTemplate && !invoiced && liTypeOptions.length > 0
        ? rows.filter((r) => !!r.item && !liTypeValueSet.has(r.item)).map((r) => r.item)
        : [],
    [isTemplate, invoiced, liTypeOptions.length, liTypeValueSet, rows],
  );
  // Hard-block the footer Save while any line carries NO Item at all.
  //
  // Unconditional — orders and templates, invoiced or not — because unlike the
  // invalid-Item block above there is no legacy noise to tolerate: every read path
  // filters `liitem <> ''` (the list endpoint, the batch response, and the
  // order-details seed), so a blank row can never arrive from the server. One sitting
  // in this grid is always one this session added or cleared.
  //
  // Clearing the Item is NOT how a line is removed, even though that is exactly how V1
  // deletes one — blank the row, keep the slot. Here it blanks only the NAME: qty, rate
  // and total survive, `recalc_invoice_total` sums every row with no filter, and the
  // result is a charge that bills real money, shows up in the invoice total, and is
  // invisible and uneditable everywhere afterwards. Two reached prod this way ($525 on
  // 5107809, $40 on 5059495 — the latter already invoiced). The ✕ in Actions is the
  // delete path, which is what the message says.
  //
  // Positions, not names: a blank row has no name to quote back, and the Line # column
  // is this same 1-based index.
  const blankItemLines = useMemo(
    () => rows.map((r, i) => (String(r.item ?? '').trim() ? 0 : i + 1)).filter(Boolean),
    [rows],
  );
  const setExtraSaveErrors = editing?.setExtraSaveErrors;
  // Whether the invalid line would actually be WRITTEN. The save only sends the
  // line-item section when the draft is dirty, so a pre-existing bogus Item on an
  // untouched invoice blocks nothing — see the unmount rule below.
  const lineItemsDirtyRef = useRef(false);
  lineItemsDirtyRef.current = lineItemsDirty;
  useEffect(() => {
    if (!setExtraSaveErrors) return;
    const errors =
      invalidMainInvoiceItems.length > 0
        ? [
            `Invoice line item${invalidMainInvoiceItems.length > 1 ? 's' : ''} not in the ` +
              `invoice item list: ${invalidMainInvoiceItems.join(', ')}. ` +
              'Pick a valid Item or remove the line before saving.',
          ]
        : [];
    setExtraSaveErrors(INVOICE_LINE_ITEM_INVALID_ERROR, errors);
    // The block must OUTLIVE this tab when it's guarding an actual write. The cleanup
    // used to clear it unconditionally, and this tab unmounts on every tab switch — so
    // stepping over to Order Info and saving persisted the bogus Item the block exists
    // to stop, silently (bugfix #33440). It survives only while the line-item draft is
    // dirty, which is exactly when the save would send it: a legacy bad Item on an
    // untouched invoice still flags while you're on this tab (unchanged) without
    // making the order unsavable from every other one. A dirty draft can only be
    // edited here, so returning to fix it recomputes and lifts the block.
    return () => {
      if (!lineItemsDirtyRef.current) setExtraSaveErrors(INVOICE_LINE_ITEM_INVALID_ERROR, []);
    };
  }, [setExtraSaveErrors, invalidMainInvoiceItems]);

  // Same shape as the invalid-Item block above, including the dirty-outlives-unmount
  // cleanup: this tab unmounts on every tab switch, so clearing unconditionally would
  // let the user step over to Order Info and save the very row the block exists to stop.
  useEffect(() => {
    if (!setExtraSaveErrors) return;
    const errors =
      blankItemLines.length > 0
        ? [
            `Invoice line item${blankItemLines.length > 1 ? 's have' : ' has'} no Item: ` +
              `line${blankItemLines.length > 1 ? 's' : ''} ${blankItemLines.join(', ')}. ` +
              'Pick an Item, or remove the line with the ✕ button — clearing the Item ' +
              'does not delete it.',
          ]
        : [];
    setExtraSaveErrors(INVOICE_LINE_ITEM_BLANK_ERROR, errors);
    return () => {
      if (!lineItemsDirtyRef.current) setExtraSaveErrors(INVOICE_LINE_ITEM_BLANK_ERROR, []);
    };
  }, [setExtraSaveErrors, blankItemLines]);

  // Invoice Total shown in the line-items header. When editable it's the live sum
  // of the current draft rows (so it recalculates as lines are added/removed/
  // recomputed); when read-only it's the saved header value. The backend re-sums
  // invoice.invoicetotal from the same line totals on save, so the two converge.
  const liveInvoiceTotal = invoiceEditable
    ? rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0)
    : inv?.invoiceTotal ?? 0;

  // ── quote charges (read-only reference pulled from the linked quote) ───────
  // The EFFECTIVE quote, not the saved one. The Quote # picker stages through
  // editedOrderFields (uh/hv) and commits on the footer Save, so reading `inv`
  // here kept this panel on "No linked quote" until that save landed: the charges
  // for a quote the user had just picked — and could see sitting in the field
  // right above — were a save away.
  //
  // Safe to follow a staged pair because everything downstream of it is read-only
  // reference (the charge list, the Dray/FSC base rows, the dray-rate mismatch
  // flag) or an explicit "+ Add to Invoice" click. Nothing here auto-stages a line
  // item. The picker also always sets both halves together, so the pair can never
  // be half-staged.
  const linkedQuoteNumber = Number(hv('quoteNumber')) || 0;
  const storedRevisionNumber = Number(hv('revisionNumber')) || 0;
  // A TEMPLATE pins a quote NUMBER but usually not a revision — the revision is
  // chosen when an order is created from it, so the stored pair is (quote, 0).
  // That is the normal state, not an edge case: 48,240 of the 50,621 quote-linked
  // templates in prod look like that, versus 53 of 1.05M real orders. Both the
  // quote link and everything in this panel resolve on a revision, so without a
  // fallback the overwhelming majority of templates showed a Quote # that could
  // not be clicked and a charge list that read "No linked quote".
  //
  // The server resolves the quote's CURRENT revision into quoteCurrentRevision for
  // exactly this — masterquote.currentrevisionnumber, not the highest revision
  // number, so a revision that is queued for a future effective date is not
  // previewed as though it were live. DISPLAY ONLY — it is never written into
  // editedOrderFields, so the template's stored revisionNumber stays 0 and the
  // Revision field below keeps showing what is actually saved.
  //
  // Only applies while the effective quote number still matches the saved one: if
  // the user stages a DIFFERENT quote through the picker, that quote's revision
  // comes with it (the picker always sets both halves), and a fallback resolved
  // for the old quote would silently mislabel the new one's charges.
  const templateFallbackRevision =
    isTemplate && linkedQuoteNumber > 0 && linkedQuoteNumber === (inv?.quoteNumber ?? 0)
      ? inv?.quoteCurrentRevision ?? 0
      : 0;
  const linkedRevisionNumber = storedRevisionNumber || templateFallbackRevision;
  // True when the revision on screen was resolved rather than stored — the panel
  // says so, so nobody reads these charges as a revision the template pins.
  const usingFallbackRevision = !storedRevisionNumber && templateFallbackRevision > 0;
  const hasLinkedQuote = linkedQuoteNumber > 0 && linkedRevisionNumber > 0;
  // Legacy v2 quote cross-reference. Shown in the "No linked quote" slot ONLY when
  // the v1 quote number is blank — a migrated v2 quote populates the v1 number, so
  // a blank v1 + present v2 means it was never resolved to a v1 quote with charges.
  const v2QuoteNumber = inv?.v2QuoteNumber ?? 0;
  const showV2QuoteRef = linkedQuoteNumber <= 0 && v2QuoteNumber > 0;
  const v2QuoteRef = showV2QuoteRef
    ? formatV2QuoteRef(v2QuoteNumber, inv?.v2RevisionNumber ?? 0, inv?.v2Lane ?? '')
    : '';
  const [quoteCharges, setQuoteCharges] = useState<TariffRateRow[]>([]);
  const [quoteSalesRep, setQuoteSalesRep] = useState('');
  // Comments card: the lane's Comments and the quote header's Internal Comments.
  const [quoteComments, setQuoteComments] = useState('');
  const [quoteInternalNotes, setQuoteInternalNotes] = useState('');
  const [quoteChargesLoading, setQuoteChargesLoading] = useState(false);
  const [quoteChargesError, setQuoteChargesError] = useState<string | null>(null);
  // Bumped after an edit in the quote drawer so the charges list refreshes.
  const [quoteChargesNonce, setQuoteChargesNonce] = useState(0);
  const [quoteDrawerOpen, setQuoteDrawerOpen] = useState(false);
  // The linked quote's real type, from the quote this tab already loads for the Quote
  // Charges panel. The drawer renders a warehouse quote differently from a direct one,
  // so handing it a hardcoded 'DIRECT' (as this did) told it the wrong thing about every
  // warehouse pro.
  const [quoteType, setQuoteType] = useState('');

  // ── rebill/credit (V1 "Rebill/Credit Info" popups) ─────────────────────────
  // Count badge seeds from the invoiceMain payload and re-reads after modal writes.
  const [rebillCount, setRebillCount] = useState<number>(inv?.rebillCount ?? 0);
  const [rebillListOpen, setRebillListOpen] = useState(false);
  const [rebillEditOpen, setRebillEditOpen] = useState(false);
  const [rebillEditRecno, setRebillEditRecno] = useState<number | null>(null);
  const [rebillRefreshKey, setRebillRefreshKey] = useState(0);
  const refreshRebillCount = useCallback(() => {
    if (!numericPronumber) return;
    rebillCreditService
      .list(numericPronumber)
      .then((res) => setRebillCount(res.count))
      .catch(() => {
        /* leave prior count on error */
      });
    setRebillRefreshKey((k) => k + 1);
  }, [numericPronumber]);

  // ── accessorials (V1 "Accessorials" section) ───────────────────────────────
  // Inline read-only table (non-deleted rows, click-to-edit) + an Add button.
  // Seeded from order details for an instant, template-aware render; refined by
  // the accessorials service (adds masExportDate for the "paid out" highlight and
  // refreshes after add/edit without a full order reload).
  const [accList, setAccList] = useState<AccessorialListRow[]>([]);
  const [accEditOpen, setAccEditOpen] = useState(false);
  const [accEditRecno, setAccEditRecno] = useState<number | null>(null);
  //: Changes the Invoice Estimate staged into the accessorial modal on open. Cleared
  //: whenever the modal is opened any other way, so a proposal from one visit cannot
  //: leak onto the next accessorial someone edits.
  const [accStagedChanges, setAccStagedChanges] = useState<StagedChange[] | null>(null);
  const [accRefreshKey, setAccRefreshKey] = useState(0);
  const refreshAcc = useCallback(() => setAccRefreshKey((k) => k + 1), []);
  // Non-deleted rows only (V1's list SQL filters deleted='N').
  const accRows = useMemo(() => accList.filter((a) => a.deleted !== 'Y'), [accList]);

  // Seed the inline list from order details (immediate; also the template path).
  useEffect(() => {
    setAccList(
      (d.accessorialInvoices ?? []).map((a) => ({
        recno: a.recno,
        accessorialNum: a.accessorialNum,
        billToName: a.billToName,
        billFromSystem: a.billFromSystem,
        doNotBillReason: a.doNotBillReason,
        invoiceDate: a.invoiceDate,
        // Seeded from order details so the row paints the right colour immediately;
        // the service refine re-reads the same value (no red→black flash).
        masExportDate: a.masExportDate ?? '',
        invoiceTotal: a.invoiceTotal,
        deleted: 'N',
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  // Refine via the service: adds masExportDate (paid-out flag) and re-reads after a
  // modal save (accRefreshKey bump). Numeric orders always refine; a template only
  // when it's editable (so the post-save refresh works) — a read-only template
  // mount keeps the seeded list (masExportDate is moot for a template) and skips the
  // extra GET. The accessorials endpoint is template-aware (reads templateinvoice).
  const accPro: number | string | null =
    numericPronumber ?? (canPersistTemplate ? (pronumber ?? null) : null);
  useEffect(() => {
    if (accPro == null) return;
    let cancelled = false;
    accessorialsService
      .list(accPro)
      .then((res) => { if (!cancelled) setAccList(res.records ?? []); })
      .catch(() => { /* keep the seeded list on error */ });
    return () => { cancelled = true; };
  }, [accPro, accRefreshKey]);
  // Dray + fuel fields from the linked quote (drives the Dray + FSC base rows).
  const [quoteFuel, setQuoteFuel] = useState<{
    drayRate: number;
    drayWithCharges: number;
    miles: number;
    fscType: string;
    fscRate: string;
    fscTariff: number | null;
    deliveryZone: string;
    fuelCalc: string;
  } | null>(null);
  // Server-computed FSC (rate + $ adjustment) for the fuel-calc date.
  const [fscCalc, setFscCalc] = useState<FscResult | null>(null);
  // The date that FSC was priced as of ("MM/DD/YYYY"), shown on the row label —
  // the rate moves week to week, so which date was used is not inferable.
  const [fscAsOf, setFscAsOf] = useState('');

  useEffect(() => {
    setQuoteSalesRep('');
    setQuoteComments('');
    setQuoteInternalNotes('');
    if (!hasLinkedQuote) {
      setQuoteCharges([]);
      setQuoteChargesError(null);
      setQuoteChargesLoading(false);
      setQuoteFuel(null);
      setQuoteType('');
      setFscCalc(null);
      setFscAsOf('');
      return;
    }
    let cancelled = false;
    setQuoteChargesLoading(true);
    setQuoteChargesError(null);
    quoteService
      .getQuote(linkedQuoteNumber, linkedRevisionNumber)
      .then((q) => {
        if (cancelled) return;
        setQuoteCharges(q.tariffRates ?? []);
        setQuoteType(q.quoteType ?? '');
        setQuoteSalesRep(q.salesRep?.trim() ?? '');
        setQuoteComments(q.direct?.comments?.trim() ?? '');
        setQuoteInternalNotes(q.internalComments?.trim() ?? '');
        const dir = q.direct;
        setQuoteFuel(
          dir
            ? {
                drayRate: Number.parseFloat(dir.drayRate) || 0,
                drayWithCharges: Number.parseFloat(dir.drayWithCharges) || 0,
                miles: Number.parseFloat(dir.deliverToMiles) || 0,
                fscType: dir.fscType || '',
                fscRate: dir.fscRate || '',
                fscTariff: dir.fscTariff ?? null,
                deliveryZone: dir.deliveryZone || '',
                fuelCalc: q.fuelCalc || '',
              }
            : null,
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setQuoteChargesError(
            isWarehouseQuoteError(err) ? WAREHOUSE_QUOTE_MESSAGE : 'Failed to load quote charges',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setQuoteChargesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasLinkedQuote, linkedQuoteNumber, linkedRevisionNumber, quoteChargesNonce]);

  // Compute the fuel surcharge as of the fuel-calc date (delivery/pickup/create/
  // etc, with a today fallback when the milestone hasn't happened). The FSC math
  // is server-side (tariff bands), so we resolve the date here and ask the API.
  useEffect(() => {
    const fscType = (quoteFuel?.fscType ?? '').trim().toUpperCase();
    if (!quoteFuel || !fscType || fscType === 'FLAT') {
      setFscCalc(null);
      setFscAsOf('');
      return;
    }
    const legArrival = (legnum?: number): Date | null => {
      if (legnum == null) return null;
      const leg = d.legs?.find((l) => l.legnum === legnum);
      return leg?.destArrivalDateTime ? parseMdYHm(leg.destArrivalDateTime) : null;
    };
    const { date } = resolveFscDate(
      quoteFuel.fuelCalc,
      {
        delivery: legArrival(d.deliveryLeg),
        pickup: legArrival(d.originateLeg),
        terminate: legArrival(d.terminateLeg),
        create: d.createdDatetime ? parseMdYHm(d.createdDatetime) : null,
      },
      new Date(),
    );
    setFscAsOf(formatMdYDate(date));
    // basis=invoice: resolve with the RECONCILER's rules, not the quote form's.
    // v1 prices the two off different fuel weeks — the invoice anchors on Monday
    // and shifts a Monday date back a full week — so a Monday delivery differs by
    // a whole week (PRO 5061777: 60.5% billed vs 63.5% on the quote-form rule).
    // This panel previews the invoice, so it has to ask for the invoice's rules.
    // liitem tells the reconciler which rate column to pick; it keys off the
    // invoice's base dray line, falling back to the prod-dominant CONTAINER DRAY.
    const deliveryArrival = legArrival(d.deliveryLeg);
    let cancelled = false;
    quoteService
      .getFsc({
        fsctype: quoteFuel.fscType,
        fsctariff: quoteFuel.fscTariff ?? undefined,
        deliveryzone: quoteFuel.deliveryZone,
        fscrate: Number.parseFloat(quoteFuel.fscRate) || 0,
        dray: quoteFuel.drayWithCharges || quoteFuel.drayRate,
        miles: quoteFuel.miles,
        as_of: toIsoDate(date),
        basis: 'invoice',
        quotetype: 'DIRECT',
        liitem: findBaseDrayItem(rows)?.item || 'CONTAINER DRAY',
        ...(deliveryArrival ? { delivery_date: toIsoDate(deliveryArrival) } : {}),
      })
      .then((res) => {
        if (!cancelled) setFscCalc(res);
      })
      .catch(() => {
        if (!cancelled) setFscCalc(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteFuel, details]);

  // Minimal lane the quote drawer needs to load the full quote by number + rev.
  const quoteDrawerLane = useMemo<QuoteListRow | null>(() => {
    if (!hasLinkedQuote) return null;
    return {
      quoteId: `${linkedQuoteNumber}-${linkedRevisionNumber}`,
      quoteNumber: linkedQuoteNumber,
      revisionNumber: linkedRevisionNumber,
      quoteType: (quoteType || 'DIRECT') as QuoteListRow['quoteType'],
      customer: '',
      billTo: '',
      lane: '',
      csr: '',
      effective: '',
      expires: '',
      pros: 0,
      status: 'UNSECURED',
      hot: false,
    } as QuoteListRow;
  }, [hasLinkedQuote, linkedQuoteNumber, linkedRevisionNumber, quoteType]);

  // The engine's answer for the callouts, fetched once per pro and refreshed after each
  // save. Deliberately NOT re-fetched on every row edit: the live check that keeps the
  // callouts honest between fetches is the presence suppression in
  // `suggestionsFromEstimate`, which costs nothing. Failures are silent — a callout is
  // advisory, and an error banner on a tab the user came to for something else is worse
  // than no callout.
  const [calloutEstimate, setCalloutEstimate] = useState<InvoiceEstimate | null>(null);
  const calloutVersion = editing?.saveVersion ?? 0;
  // Bumped after staging, so the callouts reflect what was just staged.
  const [calloutNonce, setCalloutNonce] = useState(0);
  useEffect(() => {
    if (numericPronumber == null) {
      setCalloutEstimate(null);
      return;
    }
    let cancelled = false;
    // A BILLED main invoice is answered by the PUBLISHED estimate first: its own
    // archived snapshot, taken while it was still open, carries the answer. When the
    // cache has nothing yet — billed since the last sweep, with an accessorial still
    // open — the live compute fills in, answering billed documents from their snapshots
    // and correcting the open ones.
    (invoiced
      ? invoiceEstimateService.cached(numericPronumber)
        .then((c) => c ?? invoiceEstimateService.estimate(numericPronumber))
      : invoiceEstimateService.estimate(numericPronumber))
      .then((est) => {
        if (!cancelled) setCalloutEstimate(est);
      })
      .catch(() => {
        if (!cancelled) setCalloutEstimate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [numericPronumber, calloutVersion, calloutNonce, invoiced]);

  // ── Line-item overrides ──────────────────────────────────────────────────────
  // A recorded decision that the estimate/reconciler must leave a charge alone. Keyed
  // by line-item TYPE server-side, so the lookup here is by name too.
  const [overrides, setOverrides] = useState<LineItemOverride[]>([]);
  // `invoiceRecno` is which DOCUMENT the decision is being taken on — not provenance:
  // it resolves the quote the override binds to (migration 526). Absent => the main
  // invoice, which is every row on this tab.
  const [overrideTarget, setOverrideTarget] = useState<
    { item: string; recno?: number; invoiceRecno?: number } | null>(null);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  useEffect(() => {
    if (numericPronumber == null) {
      setOverrides([]);
      return;
    }
    let cancelled = false;
    invoiceOverrideService
      .list(numericPronumber)
      .then((list) => {
        if (!cancelled) setOverrides(list);
      })
      .catch(() => {
        if (!cancelled) setOverrides([]);
      });
    return () => {
      cancelled = true;
    };
  }, [numericPronumber, calloutVersion, calloutNonce, invoiced]);

  /** Charges with an override but NO invoice line — declined outright. They have nowhere
   *  to appear in the line-item grid, so this is the only place they can be seen.
   *
   *  A UNION of two sources, because neither is complete on its own:
   *
   *    THE ESTIMATE knows what a charge would have been worth, so it leads — but it only
   *    lists overrides against a charge it is CURRENTLY PROPOSING. An override on a
   *    charge the engine has no opinion about produces nothing, and on an invoiced order
   *    the engine refuses to run at all ("already invoiced / exported"), so it produces
   *    nothing whatsoever. Both cases left a recorded decision invisible on the very
   *    order it was made against — pro 5111469's BOBTAIL and pro 5110886's CHASSIS
   *    MANAGEMENT were both live examples.
   *
   *    THE STORED OVERRIDES are always there, and since migration 490 they carry the
   *    amount that was captured when the decision was made. That is what makes this
   *    fallback worth showing rather than a bare "overridden" label.
   *
   *  Estimate rows win on a name clash: a live projection is fresher than a stored
   *  capture. Both are filtered against the live invoice rows, so a charge that gains a
   *  line mid-edit stops appearing here either way. */
  /** The override strip under Quote Charges: every override on the pro, whatever the
   *  invoice's state.
   *
   *  A billed override is therefore reachable from two places — this strip and the
   *  row's own Edit button — while the rows are still editable. That is deliberate (see
   *  selectOverrideCallouts): both open the same modal on the same record, and the
   *  strip being complete at every stage is worth more than the duplication costs.
   *
   *  `calloutEstimate` contributes nothing after invoicing — the live estimate refuses a
   *  billed order and returns a skip with no `rejected` — so on a locked invoice the
   *  whole list comes from `overrides`, which is the record that outlives the projection.
   */
  const overrideCallouts = useMemo(
    () => selectOverrideCallouts(
      calloutEstimate?.rejected ?? [], overrides, rows.map((r) => r.item)),
    [calloutEstimate, rows, overrides],
  )

  const overrideByItem = useMemo(() => {
    const m = new Map<string, LineItemOverride>();
    for (const o of overrides) m.set((o.liitem ?? '').trim().toUpperCase(), o);
    return m;
  }, [overrides]);

  /** The standing override for whatever row the dialog is pointed at, if any. Its
   *  presence turns the dialog into an editor rather than a creator. */
  const editingOverride = useMemo(() => {
    if (!overrideTarget) return null;
    return overrideByItem.get(overrideTarget.item.trim().toUpperCase()) ?? null;
  }, [overrideTarget, overrideByItem]);

  // Re-agree a drifted override at what the charge is worth now — the way out of the
  // "needs review" state that refuses Pending Approval -> RESOLVED.
  //
  // The estimate line carries the ITEM, not the override's recno, so the standing
  // decision is looked up in the list this tab already holds. Nothing to confirm and
  // nothing to type: the figure is what it is, and the user is saying the arrangement
  // still stands against it.
  const reverifyOverride = async (liitem: string) => {
    if (numericPronumber == null) return;
    const key = liitem.trim().toUpperCase();
    const standing = overrides.find((o) => (o.liitem || '').trim().toUpperCase() === key);
    if (!standing) return;
    setOverrideSaving(true);
    setOverrideError(null);
    try {
      await invoiceOverrideService.reverify(numericPronumber, standing.recno);
      // Both, for the same reason confirmOverride refreshes both: the callouts and the
      // projection were computed against the figure that just got re-agreed.
      setCalloutNonce((n) => n + 1);
      if (reconcileOpen) await loadEstimate({ live: true, keepStale: true });
    } catch (e: any) {
      setOverrideError(e?.response?.data?.detail || 'Failed to re-verify the override.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const confirmOverride = async (reason: string, comments: string) => {
    if (numericPronumber == null || !overrideTarget) return;
    setOverrideSaving(true);
    setOverrideError(null);
    try {
      if (editingOverride) {
        // Reword in place: the decision stands, only its wording changed, so the
        // original author and date survive.
        await invoiceOverrideService.update(
          numericPronumber, editingOverride.recno, reason, comments);
      } else {
        await invoiceOverrideService.create(numericPronumber, {
          liitem: overrideTarget.item,
          reason,
          comments,
          lineItemRecno: overrideTarget.recno ?? 0,
          invoiceRecno: overrideTarget.invoiceRecno ?? 0,
        });
      }
      setOverrideTarget(null);
      // Re-ask for both the overrides and the callouts: what the engine proposes has
      // just changed, so a stale callout would contradict the decision just recorded.
      setCalloutNonce((n) => n + 1);
      // ...and refresh the estimate underneath, so the row the user just overrode turns
      // into "Overridden" in front of them rather than on some later re-open.
      if (reconcileOpen) await loadEstimate({ live: true, keepStale: true });
    } catch (e: any) {
      setOverrideError(e?.response?.data?.detail || 'Failed to record the override.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const deleteOverride = async () => {
    if (numericPronumber == null || !editingOverride) return;
    setOverrideSaving(true);
    setOverrideError(null);
    try {
      await invoiceOverrideService.revoke(numericPronumber, editingOverride.recno);
      setOverrideTarget(null);
      setCalloutNonce((n) => n + 1);
      if (reconcileOpen) await loadEstimate({ live: true, keepStale: true });
    } catch (e: any) {
      setOverrideError(e?.response?.data?.detail || 'Failed to remove the override.');
    } finally {
      setOverrideSaving(false);
    }
  };

  // Charges this order has earned that the invoice doesn't carry — from the SAME
  // server-side engine as the Invoice Estimate, so the callouts and the modal can't
  // disagree. It also means chassis and fuel now appear here; the old client-side
  // rules could never see them.
  //
  // The server is asked once per pro (and again after each save); the suppression
  // below re-runs against `rows` on every render, so a charge the user just added
  // stops being flagged immediately without a request per keystroke.
  const chargeSuggestions = useMemo(
    () => suggestionsFromEstimate(calloutEstimate, rows),
    [calloutEstimate, rows],
  );
  /** Which document each charge belongs to, so a callout opens the override dialog on
   *  the charge's OWN invoice rather than letting the server fall back to the main one.
   *  See `overrideDocByItem` — getting this wrong refuses an override on an open
   *  accessorial behind a billed main invoice. */
  const overrideDocForItem = useMemo(
    () => overrideDocByItem(calloutEstimate),
    [calloutEstimate],
  );
  // Map each suggestion to the quote-charge row it came from, so that row can
  // be highlighted with an inline "+ Add to Invoice" action. Derived charges
  // (chassis / fuel) have no quote-charge row and stay banner-only. A removal is
  // never one of these: highlighting its quote charge would offer to ADD the very
  // line the engine says to take off.
  const suggestionByChargeId = useMemo(() => {
    const m = new Map<number, ChargeSuggestion>();
    for (const s of chargeSuggestions) {
      if (s.kind !== 'remove' && s.sourceChargeId != null) m.set(s.sourceChargeId, s);
    }
    return m;
  }, [chargeSuggestions]);
  // Rate mismatches now come from the ENGINE, as `update` suggestions, so the callout
  // and the estimate agree AND the estimate can actually correct the line. The
  // client-side check this replaced could only ever point at the problem.
  const mismatchByChargeId = useMemo(() => {
    const m = new Map<number, ChargeSuggestion>();
    for (const s of chargeSuggestions) {
      if (s.kind === 'update' && s.sourceChargeId != null) m.set(s.sourceChargeId, s);
    }
    return m;
  }, [chargeSuggestions]);

  // Show the Quote Charges "Action" column (a "+ Add to Invoice" button per
  // charge) whenever line items are editable (numeric order, not yet invoiced).
  const showChargeActions = lineItemsEditable;

  // Base pricing rows (Dray + calculated FSC) at the top of the Quote Charges table.
  // On a real ORDER they stay display-only: the invoice already carries these two lines,
  // and neither is a charge the user "adds" — the dray line comes with the order and the
  // FSC rate is written by automation onto a line that must already exist (v1
  // fsctypecalculation is update-only). A TEMPLATE is the opposite case: it IS the
  // initial setup of the invoice every order built from it inherits, so the two base
  // lines have to be creatable somewhere, and today that means typing them by hand.
  const drayAmt = quoteFuel?.drayRate ?? 0;
  const showDrayRow = hasLinkedQuote && drayAmt > 0;
  const fscUnit = quoteFuel ? fscRateUnit(quoteFuel.fscType, fscCalc?.ratetype ?? null) : '';
  const fscPct = fscCalc?.fscRate ?? (quoteFuel ? Number.parseFloat(quoteFuel.fscRate) || 0 : 0);
  const fscAdj = fscCalc?.adjustment ?? 0;
  // The FSC row shows on an order only when there's a surcharge to show. A template
  // shows it whenever a quote is linked, EVEN AT 0%: the rate is exactly what isn't
  // known yet when the skeleton is built (98.3% of template fuel lines carry rate 0 and
  // let automation fill it), so hiding the row at 0 would hide the button precisely
  // when it's needed. The dray row keeps its `> 0` gate — a base dray with no rate on
  // the quote has nothing to seed, and the ordinary Add Line covers that case.
  const showFscRow = hasLinkedQuote && (fscAdj > 0 || isTemplate);
  const hasBaseRows = showDrayRow || showFscRow;
  // Template-only: offer the two base charges as one-click adds.
  const canAddBaseCharges = isTemplate && lineItemsEditable;
  // …and only while they're missing. The quote-charge rows below don't check this (a
  // charge can legitimately repeat), but a second base dray or fuel line is never right.
  const hasBaseDrayRow = useMemo(() => !!findBaseDrayItem(rows), [rows]);
  const hasFuelRow = useMemo(
    () => rows.some((r) => (r.item ?? '').trim().toUpperCase() === BASE_FUEL_ITEM),
    [rows],
  );
  /** Why no chassis management fee is being suggested. The server sends this only when
   *  the reason is worth repeating — a quote-sourced one (billed IN RATE, a $0.00 rate)
   *  is already visible on the charge's own row in the table above, so it is filtered
   *  server-side and both endpoints agree about it. */
  const chassisSkip = calloutEstimate?.chassisSkipReason || '';

  // The invoice is CLOSED and the lock is actually in force. Distinct from
  // `!lineItemsEditable`, which is also true for a reader without invoice-write on an
  // open order, and from `invoiced` alone, which stays true while an Admin Override has
  // released the lock. Only this combination means "billed, and nothing here can change".
  const invoiceLocked = invoiced && !lineItemsEditable;

  // Rate-mismatch check: the invoice's base drayage line vs the quote dray rate.
  //
  // AN OVERRIDE SILENCES IT, which has to be applied here rather than inside the
  // detector. Every other callout on this screen comes from the server engine, where
  // `reconcile_from_state` filters the ops through this pro's overrides once, so they
  // all respect an override for free. This is the last client-side rate check — the base
  // dray rate lives on the quote HEADER rather than among its additional charges, so it
  // never moved to the engine with the per-charge mismatches — and it was the only one
  // still telling a user their deliberate, recorded decision was a mistake.
  //
  // Keyed on whatever line `findBaseDrayItem` actually matched rather than the literal
  // 'CONTAINER DRAY'. An override is recorded against the line-item NAME, and the
  // detector matches any item containing DRAY that is not ADDITIONAL or TRANSLOAD — so
  // reading the override off the matched line keeps the two in step by construction.
  // Every one of prod's 989,570 base dray lines is 'CONTAINER DRAY' today, which is
  // exactly why hardcoding it would pass every test and fail the day one is not.
  const drayMismatch = useMemo(() => {
    const mismatch = detectDrayRateMismatch(rows, quoteFuel?.drayRate ?? null);
    if (!mismatch) return null;
    const key = (findBaseDrayItem(rows)?.item ?? '').trim().toUpperCase();
    return key && overrideByItem.has(key) ? null : mismatch;
  }, [rows, quoteFuel, overrideByItem]);
  // Darker divider under the LAST base row, separating the Dray/FSC block from
  // the additional charge line items. On the Dray row only when FSC is absent.
  const BASE_SEP_BORDER = 'border-slate-300 dark:border-gray-600';
  const BASE_ROW_BORDER = 'border-slate-100 dark:border-gray-700';
  const drayBorder = showFscRow ? BASE_ROW_BORDER : BASE_SEP_BORDER;

  /**
   * Repricing a line on an APPROVED invoice sends it back to Pending Approval, here in
   * the form (Lee, 2026-10-19).
   *
   * The backend reverts it too — in the line-item writers for the paths that never
   * touch this form (change-all, the accessorial modal, saverule effects, the
   * reconciler) and in the sweep for charges that move with no save at all. This is the
   * MANUAL half, and it belongs in the form for two reasons the backend cannot give:
   * the user SEES the dropdown change before they save rather than discovering it
   * afterwards, and it rides the ordinary field-edit payload, so it behaves the same
   * whichever save path is live instead of needing each one plumbed separately.
   *
   * Only RESOLVED moves, and only on a figure: 'Y' is already pending, 'N' is a
   * document nobody asked to approve, and re-typing a description reprices nothing.
   *
   * A user who then picks RESOLVED themselves overwrites this — their explicit choice
   * is the later edit, and whether it is allowed is the approval gate's call, not this
   * one's.
   */
  const flipApprovalOnReprice = () => {
    if (!editing?.updateOrderField) return;
    if (str(hv('pendingApproval')) !== 'RESOLVED') return;
    uh('pendingApproval', 'Y');
  };

  const updateRow = (key: string, field: keyof EditingRow, value: unknown) => {
    if (MONEY_FIELDS.has(field as string)) flipApprovalOnReprice();
    setRows((prev) =>
      prev.map((r) => {
        if (r._key !== key) return r;
        const next = { ...r, [field]: value } as EditingRow;
        // Mirror V1's invoice_calctotal: the total is always derived from qty,
        // rate, and rate type (PCT % divides by 100). It's never hand-edited, so
        // recompute it whenever any of the three inputs change.
        if (field === 'qty' || field === 'rate' || field === 'rateType') {
          next.total = computeLineTotal(next.qty, next.rate, next.rateType);
        }
        return next;
      }),
    );
  };

  const addRow = () => {
    // A blank row bills nothing yet, so it is not a repricing until something is typed
    // into it — and `updateRow` catches that.
    setRows((prev) => [...prev, { ...emptyRow(), lineNumber: nextLineNumber(prev) }]);
  };

  // Add any quote charge to the invoice line items. The item is resolved from the
  // invoice_litypes mapping (litype whose additionalcharge_recno matches this
  // charge's type); when there's no mapping we fall back to the description-based
  // guess. GL Code comes from the mapped litype's gltype.
  /** The quote's "Additional" as an invoice line's rate TYPE, with the qty and
   *  description that go with it.
   *
   *  The two vocabularies are not the same. Additional is FLAT / PER DAY / MIN / MAX;
   *  `liratetype` is DAYS / FLAT / HOURS / MILES / PCT % / GALLON. MIN and MAX are not
   *  rate types at all — they are a per-day charge with a floor or a ceiling, which is
   *  why `compute_chassis_fee` resolves both to DAYS and carries the bound in the
   *  description instead. Writing "MIN" into `liratetype` would put a value in the
   *  column that no reader understands.
   *
   *  Seeding matches what the engine would derive, so a hand-added line and a proposed
   *  one agree instead of reading as drift the moment the sweep looks:
   *    FLAT      -> FLAT, qty 1
   *    PER DAY   -> DAYS
   *    PER HOUR  -> HOURS   (waiting time; added 2026-10-21)
   *    MIN n     -> DAYS, qty n (the floor; the engine raises qty to it anyway)
   *    MAX n     -> DAYS, qty 1 (a ceiling; 1 is already under it)
   *    blank     -> FLAT, qty 1
   *
   *  BLANK MEANS FLAT, and it has to mean that HERE as well as in the engine. The
   *  engine's `resolve_additional` has always defaulted an unset Additional to FLAT
   *  (`_DEFAULT_ADDITIONAL_FEETYPE`); this side was left blank while that default was
   *  still open with Lisa, which shipped the two surfaces disagreeing — a charge added
   *  by hand came through with no rate type at all and then read as drift the moment
   *  the sweep looked at it (Lee, 2026-10-21, chassis fee on pro 5131763).
   *
   *  The default is the FALLBACK, not a mapping: an Additional this function does not
   *  recognise also lands on FLAT rather than on blank, because a line has to bill in
   *  some unit and FLAT is the one that bills the rate exactly once. */
  const quoteChargeLineShape = (c: TariffRateRow, rate: number) => {
    const additional = (c.additionalType ?? '').trim().toUpperCase();
    const days = Number(c.additionalDays) || 0;
    if (additional === 'PER DAY') return { rateType: 'DAYS', qty: 1, description: '' };
    if (additional === 'PER HOUR') return { rateType: 'HOURS', qty: 1, description: '' };
    if (additional === 'MIN' || additional === 'MAX') {
      const qty = additional === 'MIN' ? Math.max(1, days) : 1;
      const label = days ? `${additional} ${days} DAY${days === 1 ? '' : 'S'}` : '';
      return { rateType: 'DAYS', qty, description: label };
    }
    return { rateType: 'FLAT', qty: 1, description: '' };
  };

  const addQuoteChargeRow = useCallback((c: TariffRateRow) => {
    const rate = Number.parseFloat(c.rate ?? '') || 0;
    const mapped =
      c.additionalChargesRecno != null
        ? liTypeByAddlCharge.get(c.additionalChargesRecno)
        : undefined;
    const shape = quoteChargeLineShape(c, rate);
    setRows((prev) => [
      ...prev,
      {
        ...emptyRow(),
        lineNumber: nextLineNumber(prev),
        // Table Maintenance's Quote Charge Mapping, else the charge's own name — the
        // same verdict the reconciler reaches (charges.quote_charge_invoice_item).
        item: mapped ? mapped.name : (c.description ?? '').trim(),
        glCode: mapped?.glType || 'REGION',
        qty: shape.qty,
        rate,
        rateType: shape.rateType,
        description: shape.description,
        total: computeLineTotal(shape.qty, rate, shape.rateType),
      },
    ]);
  }, [liTypeByAddlCharge]);

  // Add one of the two BASE charges to a template's invoice lines. Seeded to the shape
  // prod templates actually use — measured across the 48,693 templates that carry both:
  //   CONTAINER DRAY  qty 1, the quote's dray rate, no rate type
  //   FUEL SURCHARGE  qty = the DRAY LINE'S TOTAL (96.5%), rate 0 (98.3%), 'PCT %' (96.5%)
  // The fuel qty is the load-bearing one: on a PCT % line the quantity is the amount the
  // percentage applies to, so it has to be the dray total — that is what makes v1's
  // update-only `fuelautomation` able to fill in the rate later. It's read from the
  // CURRENT rows (not the quote) so a dray line added moments ago in the same unsaved
  // session is the one it keys off; the quote rate is only the fallback.
  const addBaseChargeRow = useCallback((kind: 'dray' | 'fuel') => {
    setRows((prev) => {
      const item = kind === 'dray' ? BASE_DRAY_ITEM : BASE_FUEL_ITEM;
      const glCode = liTypeByName.get(item)?.glType || 'REGION';
      const base = { ...emptyRow(), lineNumber: nextLineNumber(prev), item, glCode };
      if (kind === 'dray') {
        return [...prev, { ...base, qty: 1, rate: drayAmt, rateType: '',
                           total: computeLineTotal(1, drayAmt, '') }];
      }
      const qty = Number(findBaseDrayItem(prev)?.total) || drayAmt;
      return [...prev, { ...base, qty, rate: 0, rateType: 'PCT %',
                         total: computeLineTotal(qty, 0, 'PCT %') }];
    });
  }, [liTypeByName, drayAmt]);

  // Remove a row from the draft (local only). New rows just vanish; existing
  // rows are soft-removed server-side (inferred from their absence) on Save.
  const removeRow = (key: string) => {
    // Taking a charge OFF an approved invoice is a repricing like any other — only
    // when the row actually billed something, so clearing a blank row somebody added
    // by mistake is not treated as one.
    setRows((prev) => {
      const row = prev.find((r) => r._key === key);
      if (row && (Number(row.total) || Number(row.rate) || Number(row.qty))) {
        flipApprovalOnReprice();
      }
      return prev.filter((r) => r._key !== key);
    });
    // Drop it from the change-all flag set too (explicit user delete, pre-save) so a
    // removed line never fans out.
    setCheckedLines((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  // The fan-out itself is NOT fired here. It used to be, off editing.saveVersion — but
  // this tab is unmounted whenever the user saves from a different tab, so that trigger
  // (and the modal it opened) simply never ran and the anchor saved alone (bugfix #33440).
  // ProDetailContainer owns it now; the flags it reads live in the change-all provider.

  // ── Invoice Estimate (on-demand; the reconciler + the operational charge rules) ──
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<InvoiceEstimate | null>(null);

  const overrideDocInvoiced = useMemo(
    () => isOverrideDocBilled(overrideTarget?.invoiceRecno, estimate?.invoices, invoiced),
    [overrideTarget, estimate, invoiced],
  );
  // _key set of rows the estimate staged — highlighted blue until the next Save.
  const [reconciledKeys, setReconciledKeys] = useState<Set<string>>(() => new Set());

  /** The form's rows as the estimate's input. Unlike the old reconcile preview this
   *  includes rows with NO recno: a charge the user just added is on screen but not yet
   *  saved, and the estimate must see it or it will keep suggesting it. */
  const formLinesForEstimate = useCallback(
    () =>
      rows
        .filter((r) => (r.item ?? '').trim())
        .map((r) => ({
          recno: r.recno,
          item: r.item,
          qty: r.qty,
          rate: r.rate,
          rateType: r.rateType,
          total: r.total,
          description: r.description,
          glCode: r.glCode,
        })),
    [rows],
  );

  /** (Re)build the estimate.
   *
   *  CACHE-FIRST, with two deliberate exceptions. The sweep publishes an estimate for
   *  every active order, so the usual open is a single indexed read and the modal and
   *  the grid columns agree by construction. But a published estimate is a projection of
   *  SAVED state, so it is the wrong answer when the form carries unsaved line edits —
   *  it would go on suggesting a charge the user has already typed in, which is exactly
   *  what making `recno` optional on the live path was built to prevent. And it may not
   *  exist at all (a new pro, or one billed since the last sweep), in which case the
   *  answer is to compute, never to render zeros.
   *
   *  `keepStale` leaves the previous projection on screen while refreshing — used after
   *  an override, where blanking the modal the user is looking at would be more
   *  disorienting than a brief stale row. `live` forces a recompute (the Refresh
   *  control). */
  const loadEstimate = useCallback(
    async (opts?: { keepStale?: boolean; live?: boolean }) => {
      if (numericPronumber == null) return;
      setReconcileLoading(true);
      setReconcileError(null);
      if (!opts?.keepStale) setEstimate(null);
      try {
        if (!opts?.live && !lineItemsDirty) {
          const cached = await invoiceEstimateService.cached(numericPronumber);
          if (cached) {
            setEstimate(cached);
            return;
          }
        }
        // A BILLED INVOICE IS NEVER RE-ESTIMATED — but the order is billed one invoice at
        // a time, so a billed MAIN invoice is not the end of it: an accessorial may still
        // be open, and the live compute keeps correcting it while answering every billed
        // document from its own archived snapshot (Lee, 2026-10-29). Reaching here on an
        // invoiced order means the cached read had nothing — the sweep has not covered it
        // yet, or nothing was ever archived.
        //
        // When the compute comes back skipped there is nothing open and nothing archived
        // (billed in V1, or before archiving began), and the honest reply is to say so.
        const live = await invoiceEstimateService.estimate(
          numericPronumber, formLinesForEstimate());
        if (invoiced && live?.skipped) {
          setEstimate(null);
          setReconcileError(
            'No invoice reconciliation was archived for this invoice. Archiving begins when an '
            + 'invoice is created in 360, so invoices billed earlier or in V1 have none.');
          return;
        }
        setEstimate(live);
      } catch (e: any) {
        setReconcileError(
          e?.response?.data?.detail || 'Failed to reconcile the invoice.');
      } finally {
        setReconcileLoading(false);
      }
    },
    [numericPronumber, formLinesForEstimate, lineItemsDirty, invoiced],
  );

  const openReconcile = () => {
    if (numericPronumber == null) return;
    setReconcileOpen(true);
    void loadEstimate();
  };

  const stageReconcile = (est: InvoiceEstimate) => {
    const { rows: next, stagedKeys } = applyEstimateToRows(rows, est);
    setRows(next);
    setReconciledKeys(stagedKeys);
    setReconcileOpen(false);
    // Staging answers the `update` callouts too, which presence-suppression can't —
    // so re-ask rather than leave a stale "rate is wrong" banner over a staged row.
    setCalloutNonce((n) => n + 1);
  };

  /** Stage ONE accessorial's proposal into the Accessorial modal for that document.
   *
   *  It cannot go into `rows` the way the main invoice's does — those are the MAIN
   *  invoice's lines, and the footer Save writes them there. The accessorial has its own
   *  editor and its own save (PUT /invoice-line-items?accessorialnum=N), so this hands
   *  the changes to it and lets the user review and save them in the one place that can
   *  actually write them. */
  const stageAccessorial = (group: InvoiceLineGroup) => {
    if (group.invoiceRecno == null) return;
    setAccStagedChanges(
      estimateStagedChanges(
        group.lines,
        // Already this document's lines — the modal is opened on its recno, so a second
        // filter here would only be a chance to disagree with the grouping.
        () => true,
      ),
    );
    setAccEditRecno(group.invoiceRecno);
    setAccEditOpen(true);
    setReconcileOpen(false);
  };

  return (
    <div className="space-y-3">
      {reconcileOpen && (
        <InvoiceEstimateModal
          pronumber={pronumber ?? ''}
          loading={reconcileLoading}
          error={reconcileError}
          estimate={estimate}
          onStage={stageReconcile}
          // Withheld on an archived estimate for the same reason the others are: the
          // invoice is closed and its lines are locked, so offering to stage into it
          // advertises something that cannot happen.
          onStageAccessorial={estimate?.archived ? undefined : stageAccessorial}
          // Withheld on an archived estimate like the others: re-agreeing a figure on a
          // closed invoice changes nothing that can still be billed.
          onReverify={estimate?.archived || !canOverride ? undefined : reverifyOverride}
          // Both actions are withheld on an archived estimate, which `onOverride` and
          // `onRefresh` being optional already expresses — the modal simply stops
          // rendering the override column and the Recalculate control. An override
          // changes what the engine will propose NEXT, and there is no next for a closed
          // invoice; recalculating would replace the record with a projection of today.
          onOverride={
            estimate?.archived || !canOverride
              ? undefined
              : (item, recno, invoiceRecno) =>
                  // The estimate STAYS open underneath: the override dialog claims the
                  // hotkey layer and sits above it, and on confirm the estimate refreshes
                  // in place so the row flips to Overridden while the user is still
                  // looking at it.
                  //
                  // `invoiceRecno` arrives only from an ACCESSORIAL panel; the main
                  // invoice's rows send nothing and fall back to it server-side.
                  setOverrideTarget({
                    item,
                    recno: recno ?? undefined,
                    invoiceRecno: invoiceRecno ?? undefined,
                  })
          }
          // Keep the cached figures up while recomputing — the user asked to refresh a
          // projection they are reading, not to have it replaced by a spinner.
          onRefresh={
            estimate?.archived
              ? undefined
              : () => void loadEstimate({ live: true, keepStale: true })
          }
          onClose={() => setReconcileOpen(false)}
        />
      )}
      {overrideTarget && (
        <LineItemOverrideModal
          pronumber={pronumber ?? ''}
          liitem={overrideTarget.item}
          lineItemRecno={overrideTarget.recno}
          existing={editingOverride}
          saving={overrideSaving}
          error={overrideError}
          onConfirm={confirmOverride}
          // A reader who is not an LC can still OPEN a standing override — the lock on an
          // overridden line is deliberately not permission-gated, because seeing who
          // decided and why is not a write. The dialog drops its Save and Remove controls
          // instead of offering actions the server refuses (migrations 536/537).
          readOnly={!canOverride}
          // Withdrawing is refused server-side once THIS decision's own invoice is out
          // (409), so the control is withheld rather than offered and then rejected. The
          // reason stays editable, which is the point: a badly-worded decision can be
          // corrected without erasing the record of it.
          onDelete={overrideDocInvoiced ? undefined : deleteOverride}
          deleteBlockedReason={overrideDocInvoiced
            ? 'Billed — this decision can be reworded but not withdrawn.'
            : undefined}
          onClose={() => {
            setOverrideTarget(null);
            setOverrideError(null);
          }}
        />
      )}
      <div className="iv-sections bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden">
        <div className="iv-sections-grid">
          {/* COLUMN 1 — Invoice Dates / Margin */}
          <Section title="Invoice & Margin" icon={<Icon.calendar className="w-3.5 h-3.5" />}>
            <Row>
              <Label width={LABEL_W}>Invoice / Sage Date</Label>
              <span
                className={`inline-flex items-center gap-1 flex-1 min-w-0 ${invExportHl.cls}`}
                title={invExportHl.title || undefined}
              >
                <ReadValue value={inv?.invoiceDate} mono width={88} ariaLabel="Invoice Date" />
                <ReadValue value={inv?.masExportDate} mono ariaLabel="Sage Export Date" />
              </span>
            </Row>

            <Divider />

            <div className="text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
              Margin
            </div>
            <Row>
              <Label width={LABEL_W}>Margin</Label>
              <ReadValue
                value={d.margin ? `${money(d.margin)}%` : undefined}
                mono
                ariaLabel="Margin"
              />
            </Row>
            <Row>
              <Label width={LABEL_W}>Margin Limit</Label>
              {/* Editable on a template (State 3); read-only on a real order. Rides
                  orderData -> ordersall/template.marginlimitlocal. */}
              {fieldEditable('marginLimitLocal', false) ? (
                <input
                  type="number"
                  step="0.01"
                  className={`${INPUT_CLS}`}
                  value={str(hv('marginLimitLocal'))}
                  onChange={(e) => uh('marginLimitLocal', e.target.value)}
                  placeholder="0.00"
                  aria-label="Margin Limit"
                />
              ) : (
                <span className="inline-flex items-baseline gap-2">
                  <ReadValue
                    value={d.marginLimitLocal ? money(d.marginLimitLocal) : undefined}
                    mono
                    ariaLabel="Margin Limit"
                  />
                  {/* V1 fv_marginlimit: when no explicit limit is set, show the default that
                      applies — OTR (17) for OTR orders, else Local (23). */}
                  {!Number(d.marginLimitLocal) && (
                    <span className="text-xs text-muted whitespace-nowrap">
                      {(d.otr || '').toUpperCase() === 'Y'
                        ? `OTR Limit: ${money(MARGIN_LIMIT_OTR_DEFAULT)}`
                        : `Local Limit: ${money(MARGIN_LIMIT_LOCAL_DEFAULT)}`}
                    </span>
                  )}
                </span>
              )}
            </Row>
            {/* Field 1 — Margin approval. Checkbox stamps the current user +
                (server) datetime; by/date stay read-only. Own permission gate
                (hidden until granted), and only on a real order (not a template). */}
            {!isTemplate && (
              <PermissionsContainer
                componentId={MARGIN_APPROVAL_PERMISSION_ID}
                componentName={MARGIN_APPROVAL_PERMISSION_NAME}
                defaultHidden
              >
                <Row>
                  <Label width={LABEL_W}>Approve / By / Date</Label>
                  {editable ? (
                    <label className="inline-flex items-center gap-1.5 h-6 px-1.5 cursor-pointer text-[0.75rem] select-none">
                      <input
                        type="checkbox"
                        checked={marginApproved}
                        onChange={toggleMarginApproval}
                        aria-label="Approve margin"
                        className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                      />
                      <span className="text-slate-600 dark:text-gray-300">Approved</span>
                    </label>
                  ) : (
                    <ReadValue
                      value={marginApproved ? 'Y' : undefined}
                      width={32}
                      ariaLabel="Margin Approve"
                    />
                  )}
                  <ReadValue value={str(hv('marginApprovedBy'))} ariaLabel="Margin Approved By" />
                  <ReadValue
                    value={str(hv('marginApprovedDatetime'))}
                    mono
                    width={112}
                    ariaLabel="Margin Approved Date"
                  />
                  <InvBulkCheckbox
                    enabled={fieldChangeAll}
                    editable={editable}
                    field="marginApproval"
                    label="Margin Approval"
                    checkedInv={checkedInv}
                    onToggle={toggleInv}
                  />
                </Row>
              </PermissionsContainer>
            )}

            <Divider />

            <div className="text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
              Invoice Hold
            </div>
            <OrderLcFieldGroup isTemplate={isTemplate}>
              <Row>
                <div className="w-1/2 flex items-center gap-2 min-w-0">
                  <Label width={64}>Reason</Label>
                  {holdEditable ? (
                    <select
                      className={SELECT_CLS}
                      value={str(hv('invoiceHoldReason'))}
                      onChange={(e) => uh('invoiceHoldReason', e.target.value)}
                      aria-label="Hold Reason"
                    >
                      <option value="">—</option>
                      {holdReasonOptions.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <ReadValue value={d.invoiceHoldReason} ariaLabel="Hold Reason" />
                  )}
                  <InvBulkCheckbox
                    enabled={fieldChangeAll}
                    editable={holdEditable}
                    field="invoiceHoldReason"
                    label="Hold Reason"
                    checkedInv={checkedInv}
                    onToggle={toggleInv}
                  />
                </div>
                <div className="w-1/2 flex items-center gap-2 min-w-0">
                  <Label width={90}>Last Follow Up</Label>
                  {holdEditable ? (
                    <div className="flex-1 min-w-0">
                      <DateTimePicker
                        mode="datetime"
                        inputVariant="compact"
                        selectionType="single"
                        use24Hour
                        usePortal
                        openDirection="down"
                        autoFlip
                        labelType="none"
                        ariaLabel="Last Follow Up"
                        placeholder="Date/Time"
                        className="w-full [&_div]:!min-w-0"
                        value={parseMdYHm(str(hv('invoiceHoldFollowupDatetime')))}
                        onChange={(v) =>
                          uh('invoiceHoldFollowupDatetime', formatMdYHm(v as Date | null))
                        }
                      />
                    </div>
                  ) : (
                    <ReadValue
                      value={d.invoiceHoldFollowupDatetime}
                      mono
                      ariaLabel="Last Follow Up"
                    />
                  )}
                  <InvBulkCheckbox
                    enabled={fieldChangeAll}
                    editable={holdEditable}
                    field="invoiceHoldFollowupDatetime"
                    label="Last Follow Up"
                    checkedInv={checkedInv}
                    onToggle={toggleInv}
                  />
                </div>
              </Row>
            </OrderLcFieldGroup>
          </Section>

          {/* COLUMN 2 — Required for Invoicing / Estimated Invoice Email / Accessorials */}
          <Section
            title="Required for Invoicing"
            icon={<Icon.doc className="w-3.5 h-3.5" />}
          >
            {/* Field 2 — required-for-invoicing flags: editable N/Y/RESOLVED
                dropdowns (LC-edit group), cell yellow when 'Y'. */}
            <OrderLcFieldGroup isTemplate={isTemplate}>
              <div className="iv-flags">
                <div className="iv-flags-grid gap-x-2 gap-y-0.5">
                  {REQUIRED_INVOICE_FIELDS.map(({ label, key }) => {
                    const v = str(hv(key));
                    return (
                      <InvoiceFlagSelect
                        key={key}
                        label={label}
                        value={v}
                        options={REQUIRE_OPTIONS}
                        onChange={(nv) => uh(key, nv)}
                        readOnly={!fieldEditable(key, holdEditable)}
                        highlight={v === 'Y' ? 'yellow' : undefined}
                        labelWidth={110}
                        ariaLabel={`Require ${label}`}
                        bulk={
                          <InvBulkCheckbox
                            enabled={fieldChangeAll}
                            editable={fieldEditable(key, holdEditable)}
                            field={key}
                            label={label}
                            checkedInv={checkedInv}
                            onToggle={toggleInv}
                          />
                        }
                      />
                    );
                  })}
                </div>
              </div>
            </OrderLcFieldGroup>

            <Divider />

            {/* Field 3 — COD: own permission (read-only until granted), Y/N,
                cell red when 'Y'. */}
            <PermissionsContainer
              componentId={COD_PERMISSION_ID}
              componentName={COD_PERMISSION_NAME}
              defaultDisabled
            >
              <InvoiceFlagSelect
                label="COD"
                value={str(hv('cod'))}
                options={COD_OPTIONS}
                onChange={(nv) => uh('cod', nv)}
                readOnly={!fieldEditable('cod', canEditCod)}
                highlight={str(hv('cod')) === 'Y' ? 'red' : undefined}
                labelWidth={110}
                ariaLabel="COD"
                bulk={
                  <InvBulkCheckbox
                    enabled={fieldChangeAll}
                    editable={fieldEditable('cod', canEditCod)}
                    field="cod"
                    label="COD"
                    checkedInv={checkedInv}
                    onToggle={toggleInv}
                  />
                }
              />
            </PermissionsContainer>

            <Divider />

            {/* Field 4 — Estimated Invoice Email (LC group) with email-format
                validation (same helper as ContactInfo). */}
            <OrderLcFieldGroup isTemplate={isTemplate}>
              <Row className="!items-start">
                <Label width={LABEL_W} className="mt-1">Est Inv Email</Label>
                <div className="flex-1 min-w-0">
                  {fieldEditable('estInvoiceEmail', holdEditable) ? (
                    <>
                      <input
                        type="text"
                        className={`${INPUT_CLS} ${estEmailInvalid ? 'border-red-500 dark:border-red-500' : ''}`}
                        value={str(hv('estInvoiceEmail'))}
                        onChange={(e) => uh('estInvoiceEmail', e.target.value)}
                        placeholder="name@example.com"
                        aria-label="Est Inv Email"
                      />
                      {estEmailInvalid && (
                        <div className="mt-0.5 text-[0.6875rem] text-red-600 dark:text-red-400">
                          Enter a valid email address.
                        </div>
                      )}
                    </>
                  ) : (
                    <ReadValue value={inv?.estInvoiceEmail} ariaLabel="Est Inv Email" />
                  )}
                </div>
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={fieldEditable('estInvoiceEmail', holdEditable)}
                  field="estInvoiceEmail"
                  label="Est Inv Email"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
            </OrderLcFieldGroup>

            {/* Accessorials are their own persisted records with their own save
                process — none can exist before the order does, and the server
                copies the template's set inside the create transaction. */}
            {!createMode && (<>
            <Divider />

            <Row className="!items-start">
              <Label width={LABEL_W} className="mt-1">
                Accessorials
              </Label>
              <div className="flex-1 min-w-0">
                {invoiceEditable && (
                  <div className="mb-1">
                    <InvoiceWriteGate isTemplate={isTemplate}>
                      <button
                        type="button"
                        onClick={() => {
                          setAccEditRecno(null);
                          setAccStagedChanges(null);
                          setAccEditOpen(true);
                        }}
                        aria-label="Add Accessorial"
                        title="Add Accessorial"
                        className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[0.6875rem] font-semibold text-slate-700 hover:bg-slate-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        + Add
                      </button>
                    </InvoiceWriteGate>
                  </div>
                )}
                {accRows.length === 0 ? (
                  <span className="text-[0.75rem] text-slate-400 dark:text-gray-500 italic">
                    {PLACEHOLDER}
                  </span>
                ) : (
                  <div className="border border-slate-200 dark:border-gray-700 rounded bg-slate-50 dark:bg-gray-800 max-h-[140px] overflow-y-auto">
                    <table className="w-full text-[0.71875rem]" aria-label="Accessorials">
                      <thead className="bg-slate-100 dark:bg-gray-800">
                        <tr>
                          <TH>Acc #</TH>
                          <TH>Bill To</TH>
                          <TH>Inv Date</TH>
                          <TH className="text-right">Total</TH>
                        </tr>
                      </thead>
                      <tbody>
                        {accRows.map((acc) => {
                          // V1 fv_accessorials coloring (formatview.pl:5350-5351):
                          // invoiced-not-exported = red, masexportdate set (Sage-
                          // exported) = black and wins. Shared with the main
                          // invoice cell above.
                          const hl = invoiceExportHighlight(
                            acc.invoiceDate,
                            acc.masExportDate,
                          );
                          // The whole row opens the edit modal (V1 only made the
                          // Acc # itself clickable — the full row is a bigger
                          // target and matches the rest of the drawer's grids).
                          const openEdit = () => {
                            setAccEditRecno(acc.recno);
                            setAccStagedChanges(null);
                            setAccEditOpen(true);
                          };
                          return (
                            <tr
                              key={acc.recno}
                              className={`${hl.cls} ${invoiceEditable ? 'group cursor-pointer' : ''}`}
                              title={hl.title || undefined}
                              {...(invoiceEditable
                                ? {
                                    role: 'button',
                                    tabIndex: 0,
                                    'aria-label': `Edit accessorial ${acc.accessorialNum}`,
                                    onClick: openEdit,
                                    onKeyDown: (e: React.KeyboardEvent) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openEdit();
                                      }
                                    },
                                  }
                                : {})}
                            >
                              <td className="px-2 py-1 border-b border-slate-100 dark:border-gray-700 tabular-nums">
                                {invoiceEditable ? (
                                  <span className="text-blue-600 dark:text-blue-400 group-hover:underline">
                                    {acc.accessorialNum}
                                  </span>
                                ) : (
                                  acc.accessorialNum
                                )}
                              </td>
                              <td className="px-2 py-1 border-b border-slate-100 dark:border-gray-700">
                                {acc.billToName || PLACEHOLDER}
                              </td>
                              <td className="px-2 py-1 border-b border-slate-100 dark:border-gray-700 tabular-nums">
                                {acc.invoiceDate || PLACEHOLDER}
                              </td>
                              <td className="px-2 py-1 border-b border-slate-100 dark:border-gray-700 text-right tabular-nums">
                                {money(acc.invoiceTotal)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Row>
            </>)}
          </Section>

          {/* COLUMN 3 — Bill To / Approval */}
          <Section title="Bill To & Approval" icon={<Icon.user className="w-3.5 h-3.5" />}>
            <Row>
              <Label width={LABEL_W_RIGHT}>Sage Tag</Label>
              <ReadValue value={d.billToMas90Code} mono ariaLabel="Sage Tag" />
            </Row>
            <Row className={fieldEditable('billToName', false) ? '!items-start' : undefined}>
              <Label width={LABEL_W_RIGHT} className={fieldEditable('billToName', false) ? 'mt-1' : undefined}>Bill To</Label>
              {fieldEditable('billToName', false) ? (
                // Editable on an UNAPPROVED template (State 2 — a re-quote key field).
                // Persists the billto recno via orderData (`billto`); the name is
                // read-derived. billToContactRecno is cleared so a stale contact from
                // the prior bill-to isn't kept.
                <div className="flex-1 min-w-0">
                  <BillToLookupField
                    value={{ recno: Number(hv('billTo')) || 0, name: str(hv('billToName')) || str(d.billToName) }}
                    onChange={(v) => {
                      uh('billTo', v.recno);
                      uh('billToName', v.name);
                      uh('billToContactRecno', 0);
                      uh('billToContactName', '');
                    }}
                    label=""
                  />
                </div>
              ) : (
                <ReadValue value={d.billToName} ariaLabel="Bill To" />
              )}
              {/* Pending edit wins so the pencil follows a just-picked bill-to. */}
              <EntityEditLink
                kind="billto"
                recno={Number(hv('billTo')) || billToRecno}
                label="Bill To"
              />
            </Row>
            {/* Field 5 — Bill To Contact lookup (contactinfo/billtos) + phone/email. */}
            <OrderLcFieldGroup isTemplate={isTemplate}>
              <Row className="!items-start">
                <Label width={LABEL_W_RIGHT} className="mt-1">Bill To Contact</Label>
                <div className="flex-1 min-w-0">
                  {fieldEditable('billToContactName', holdEditable) ? (
                    <select
                      className={SELECT_CLS}
                      value={effBillToContactRecno ? String(effBillToContactRecno) : ''}
                      onChange={(e) => {
                        const recno = Number(e.target.value) || 0;
                        uh('billToContactRecno', recno);
                        const c = billToContacts.find((x) => x.recno === recno);
                        uh('billToContactName', c?.contactname ?? '');
                      }}
                      aria-label="Bill To Contact"
                    >
                      <option value="">—</option>
                      {selectedBillToContact == null && effBillToContactRecno > 0 && (
                        <option value={String(effBillToContactRecno)}>
                          {str(d.billToContactName) || `#${effBillToContactRecno}`}
                        </option>
                      )}
                      {billToContacts.map((c) => (
                        <option key={c.recno} value={String(c.recno)}>
                          {c.contactname}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <ReadValue value={d.billToContactName} ariaLabel="Bill To Contact" />
                  )}
                  {(billToContactPhone || billToContactEmail) && (
                    <div className="mt-0.5 text-[0.6875rem] text-slate-500 dark:text-gray-400 leading-tight">
                      {billToContactPhone && <div>{billToContactPhone}</div>}
                      {billToContactEmail && <div className="truncate">{billToContactEmail}</div>}
                    </div>
                  )}
                </div>
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={fieldEditable('billToContactName', holdEditable)}
                  field="billToContactRecno"
                  label="Bill To Contact"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
            </OrderLcFieldGroup>
            {/* Field 6 — Bill To Ref # (open entry, LC group). */}
            <OrderLcFieldGroup isTemplate={isTemplate}>
              <Row>
                <Label width={LABEL_W_RIGHT}>Bill To Ref #</Label>
                {fieldEditable('billToRefNo', holdEditable) ? (
                  <input
                    type="text"
                    className={`${INPUT_CLS}`}
                    value={str(hv('billToRefNo'))}
                    onChange={(e) => uh('billToRefNo', e.target.value)}
                    aria-label="Bill To Ref"
                  />
                ) : (
                  <ReadValue value={inv?.billToRefNo} mono ariaLabel="Bill To Ref" />
                )}
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={fieldEditable('billToRefNo', holdEditable)}
                  field="billToRefNo"
                  label="Bill To Ref #"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
            </OrderLcFieldGroup>
            {/* Field 7 — Freight Terms dropdown (LC group). */}
            <OrderLcFieldGroup isTemplate={isTemplate}>
              <Row>
                <Label width={LABEL_W_RIGHT}>Freight Terms</Label>
                {fieldEditable('freightTerms', holdEditable) ? (
                  <select
                    className={SELECT_CLS}
                    value={str(hv('freightTerms'))}
                    onChange={(e) => uh('freightTerms', e.target.value)}
                    aria-label="Freight Terms"
                  >
                    {FREIGHT_TERMS_OPTIONS.map((o) => (
                      <option key={o} value={o}>{o || '—'}</option>
                    ))}
                  </select>
                ) : (
                  <ReadValue value={d.freightTerms} ariaLabel="Freight Terms" />
                )}
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={fieldEditable('freightTerms', holdEditable)}
                  field="freightTerms"
                  label="Freight Terms"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
            </OrderLcFieldGroup>

            <Divider />

            <InvoiceApprovalGate isTemplate={isTemplate}>
              <Row>
                <Label width={LABEL_W_RIGHT}>Approve / By / Date</Label>
                {/* aria-label is "Approve", not "Approve template": this control is
                    drawn on real ORDERS too, where it approves the invoice. The
                    read-only branch below uses the same name, and only one of the
                    two ever renders. */}
                {canToggleApproval ? (
                  <label className="inline-flex items-center gap-1.5 h-6 px-1.5 cursor-pointer text-[0.75rem] select-none">
                    <input
                      type="checkbox"
                      checked={approvedEffective}
                      onChange={toggleApproval}
                      aria-label="Approve"
                      className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                    />
                    <span className="text-slate-600 dark:text-gray-300">Approved</span>
                  </label>
                ) : (
                  <ReadValue
                    value={approvedEffective ? 'Y' : undefined}
                    width={32}
                    ariaLabel="Approve"
                  />
                )}
                {/* Approver + timestamp as a compact group: the name is bounded
                    (truncates with a tooltip) so it never pushes the timestamp off
                    the right edge, and the timestamp stays fully visible. */}
                <span
                  className="inline-flex h-6 max-w-[150px] items-center truncate px-1.5 text-[0.75rem] text-slate-900 dark:text-gray-100"
                  aria-label="Approved By"
                  title={effApprovedBy || undefined}
                >
                  {effApprovedBy || PLACEHOLDER}
                </span>
                <span
                  className="inline-flex h-6 shrink-0 items-center whitespace-nowrap px-1.5 text-[0.75rem] text-slate-900 dark:text-gray-100"
                  aria-label="Approved Date"
                >
                  {effApprovedDatetime || PLACEHOLDER}
                </span>
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={canToggleApproval}
                  field="invoiceApproval"
                  label="Invoice Approval"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
              {approvalPending && (
                <Row>
                  {/* spacer aligns the hint under the name/timestamp, not the label */}
                  <div style={{ width: LABEL_W_RIGHT }} className="shrink-0" />
                  <span className="text-[0.6875rem] italic text-amber-600 dark:text-amber-400">
                    unsaved — locks/opens on Save
                  </span>
                </Row>
              )}
            </InvoiceApprovalGate>
          </Section>
        </div>
      </div>

      {/* INVOICE OPTIONS | RATE COMMENTS | QUOTE COMMENTS ───────────────────── */}
      <div className="iv-split bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden p-4">
        <div className="iv-options-split">
          {/* LEFT PANEL — Invoice Options (max 500px, also when stacked) */}
          <div className="min-w-0 max-w-[500px]">
            <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400 mb-1">
              Invoice Options
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1 border border-slate-200 dark:border-gray-700 rounded px-3 py-2 bg-slate-50 dark:bg-gray-800">
              {/* Own component permission on a real order (invoice-bill-from —
                  seeded to today's LC audience PLUS the invoicing managers, who are
                  View on order-lc-fields), and the template-edit gate on an UNAPPROVED
                  template (State 2). The INVOICED lock still applies on top via
                  fieldEditable -> roField.
                  'DO NOT BILL' surfaces the Do-Not-Bill reason row below.
                  Wrapped like every other per-field component on this tab (COD, Do Not
                  Bill Reason, Quote #, Margin Approval) so permissions mode draws the
                  frame + handle and an admin can configure it where it lives. The hook
                  above still decides editability; this container is what makes the
                  component discoverable — without it the grant exists in the DB with
                  nothing on the page pointing at it. */}
              <PermissionsContainer
                componentId={BILL_FROM_COMPONENT_ID}
                componentName={BILL_FROM_COMPONENT_NAME}
                defaultDisabled
              >
                <Row>
                  <Label width={LABEL_W}>Bill From</Label>
                  {fieldEditable('billFromSystem', billFromEditable) ? (
                    <select
                      className={SELECT_CLS}
                      value={currentBillFrom}
                      onChange={(e) => setBillFromSystem(e.target.value)}
                      aria-label="Bill From"
                    >
                      {billFromOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <ReadValue value={inv?.billFromSystem} ariaLabel="Bill From" />
                  )}
                </Row>
              </PermissionsContainer>
              {/* Do Not Bill Reason — only meaningful when Bill From says the order
                  is not being billed, so the row is absent entirely under CMS. Its
                  own manager-only permission (read-only until granted); on a
                  template the template-edit gate is the sole authority. */}
              {isDoNotBill && (
                <PermissionsContainer
                  componentId={DO_NOT_BILL_REASON_PERMISSION_ID}
                  componentName={DO_NOT_BILL_REASON_PERMISSION_NAME}
                  defaultDisabled
                >
                  <Row>
                    <Label width={LABEL_W}>Do Not Bill</Label>
                    {fieldEditable('doNotBillReason', canEditDoNotBillReason) ? (
                      <select
                        className={SELECT_CLS}
                        value={currentDoNotBill}
                        onChange={(e) => uh('doNotBillReason', e.target.value)}
                        aria-label="Do Not Bill Reason"
                      >
                        {doNotBillOptions.map((o) => (
                          <option key={o} value={o}>{o || '—'}</option>
                        ))}
                      </select>
                    ) : (
                      <ReadValue value={inv?.doNotBillReason} ariaLabel="Do Not Bill Reason" />
                    )}
                    <InvBulkCheckbox
                      enabled={fieldChangeAll}
                      editable={fieldEditable('doNotBillReason', canEditDoNotBillReason)}
                      field="doNotBillReason"
                      label="Do-Not-Bill Reason"
                      checkedInv={checkedInv}
                      onToggle={toggleInv}
                    />
                  </Row>
                </PermissionsContainer>
              )}
              {/* Credit Amt — see `creditAmtEditable`. Digits and one decimal point
                  only, and no minus sign: V1 puts `cmsadjcheck` on this input, which
                  refuses a negative outright (common_html5.js:861). The amount is
                  stored positive and shown as a deduction on the invoice PDF. */}
              <Row>
                <Label width={LABEL_W}>Credit Amt</Label>
                {creditAmtEditable ? (
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${INPUT_CLS} text-right`}
                    value={creditAmountText}
                    onChange={(e) => uh('creditAmount', creditAmountInput(e.target.value))}
                    aria-label="Credit Amount"
                  />
                ) : (
                  <ReadValue
                    value={inv && inv.creditAmount > 0 ? money(inv.creditAmount) : undefined}
                    mono
                    ariaLabel="Credit Amount"
                  />
                )}
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={creditAmtEditable}
                  field="creditAmount"
                  label="Credit Amt"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
              {/* Credit Reason — the writable half of the credit pair, present only
                  when there IS a credit (Accessorial-modal parity). Templates stay
                  locked: creditReason has no templateFieldPolicy entry, so
                  templateFieldEditable defaults it to State 1. */}
              {hasCredit && (
                <Row>
                  <Label width={LABEL_W}>Credit Reason</Label>
                  {fieldEditable('creditReason', editable) ? (
                    <select
                      className={SELECT_CLS}
                      value={currentCreditReason}
                      onChange={(e) => uh('creditReason', e.target.value)}
                      aria-label="Credit Reason"
                    >
                      {creditReasonOptions.map((o) => (
                        <option key={o} value={o}>{o || '—'}</option>
                      ))}
                    </select>
                  ) : (
                    <ReadValue value={inv?.creditReason} ariaLabel="Credit Reason" />
                  )}
                  <InvBulkCheckbox
                    enabled={fieldChangeAll}
                    editable={fieldEditable('creditReason', editable)}
                    field="creditReason"
                    label="Credit Reason"
                    checkedInv={checkedInv}
                    onToggle={toggleInv}
                  />
                </Row>
              )}
              <Row>
                <Label width={LABEL_W}>Rebill/Credit</Label>
                {/* The column is NOT NULL DEFAULT 'N', so a blank should not occur
                    — but if one ever does it must SHOW blank rather than borrow 'N'
                    (V1 smod_forcevalueinarray). Displaying a value the field does
                    not hold is what let a blank billfromsystem hide for a month. */}
                {editable ? (
                  <select
                    className={SELECT_CLS}
                    value={str(hv('rebill'))}
                    onChange={(e) => uh('rebill', e.target.value)}
                    aria-label="Rebill"
                  >
                    {forcedSelectOption(str(hv('rebill')), REBILL_OPTIONS) && (
                      <option value="" />
                    )}
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                ) : (
                  <ReadValue value={d.rebill} width={32} ariaLabel="Rebill" />
                )}
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={editable}
                  field="rebill"
                  label="Rebill"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
                {/* Add AND the count link sit inside ONE permission gate, which is V1
                    (lib_editrecord.pl:8678-8705 draws both inside the `$acl_attr eq "w"`
                    block, so a user without write access to `rebill` sees neither). The
                    count link used to sit outside it and rendered for anyone who could
                    open a pro, while the four rebill-credit endpoints are granted to the
                    same two groups as this permission — so clicking it answered 403 and
                    the modal said "Failed to load rebill/credit records". */}
                {editable && str(hv('rebill')) === 'Y' && (
                  <PermissionsContainer
                    componentId={INVOICE_WRITE_PERMISSION_ID}
                    componentName={INVOICE_WRITE_PERMISSION_NAME}
                    className="inline-flex items-center gap-1"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setRebillEditRecno(null);
                        setRebillEditOpen(true);
                      }}
                      aria-label="Add Rebill/Credit"
                      title="Add Rebill/Credit"
                      className="inline-flex items-center justify-center h-6 w-6 shrink-0 rounded text-[0.875rem] font-semibold leading-none text-blue-600 dark:text-blue-400 hover:bg-slate-100 dark:hover:bg-gray-800"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => setRebillListOpen(true)}
                      aria-label="View Rebill/Credit records"
                      className="text-[0.75rem] text-blue-600 dark:text-blue-400 hover:underline focus:outline-none focus:underline"
                    >
                      {rebillCount}
                    </button>
                  </PermissionsContainer>
                )}
              </Row>
              {/* Missing Inv Approved — Y/N over the approver stamp (see
                  `missingInvApproved`). Same gate as Rebill/Credit; the approver
                  name and datetime are never surfaced (V1 left the datetime unset
                  and only 6 orders in 1.28M ever carried an approver). */}
              <Row>
                <Label width={LABEL_W}>Missing Inv Approved</Label>
                {editable ? (
                  <select
                    className={SELECT_CLS}
                    value={missingInvApproved}
                    onChange={(e) => setMissingInvApproved(e.target.value)}
                    aria-label="Missing Inv Approved"
                  >
                    <option value="Y">Y</option>
                    <option value="N">N</option>
                  </select>
                ) : (
                  <ReadValue value={missingInvApproved} width={32} ariaLabel="Missing Inv Approved" />
                )}
              </Row>
              {missingInvApproved === 'Y' && (
                <Row>
                  <Label width={LABEL_W}>Missing Inv Reason</Label>
                  {editable ? (
                    <select
                      className={SELECT_CLS}
                      value={currentMissingInvReason}
                      onChange={(e) => uh('missingInvoiceApprovedReason', e.target.value)}
                      aria-label="Missing Inv Reason"
                    >
                      {missingInvReasonOptions.map((o) => (
                        <option key={o} value={o}>{o || '—'}</option>
                      ))}
                    </select>
                  ) : (
                    <ReadValue value={currentMissingInvReason} ariaLabel="Missing Inv Reason" />
                  )}
                </Row>
              )}
              {/* Quote # / Revision are State-2 template fields (editable while
                  unapproved). Staged as a pair into editedOrderFields and committed by
                  the footer Save: in native mode through the saverules engine's
                  invoiceData section (fires order_quoterevision — the quote-secure
                  side effect is skipped for templates), and in cmsapi/shadow mode
                  through the template-aware /invoice-header side-channel. Either path
                  writes templateinvoice.quotenumber/revisionnumber. */}
              {/* Own permission component (Exempt scheme — see the export's
                  doc): exactly Edit = editable + required at save, Exempt /
                  un-granted = editable only, View = read-only, Hidden = row
                  gone. holdEditable (order-lc-fields) still gates edit on top. */}
              <PermissionsContainer
                componentId={QUOTE_NUMBER_PERMISSION_ID}
                componentName={QUOTE_NUMBER_PERMISSION_NAME}
              >
              <Row>
                <Label width={LABEL_W}>Quote #</Label>
                {fieldEditable('quoteNumber', canEditQuoteNumber) ? (
                  // Editable: one picker for the pair (V1 lib_editrecord.pl:9082-9152
                  // — a Quote # field with a lookup, plus a "Find Quote" spyglass).
                  // Both halves are staged together into editedOrderFields, so they
                  // reach the invoice section as a pair and commit on the footer Save.
                  // The spyglass takes the order OR the "T…" template pro — the
                  // lookup resolves either's bill-to / rate zone / delivery context.
                  <QuoteLookupField
                    quoteNumber={Number(hv('quoteNumber')) || 0}
                    revisionNumber={Number(hv('revisionNumber')) || 0}
                    onChange={(qn, rn) => {
                      uh('quoteNumber', qn);
                      uh('revisionNumber', rn);
                    }}
                    pronumber={pronumber}
                    context={editing?.quoteLookupContext}
                    className="flex-1"
                    ariaLabel="Quote Number and Revision"
                  />
                ) : (
                  <>
                    <ReadValue
                      value={inv && inv.quoteNumber ? String(inv.quoteNumber) : undefined}
                      mono
                      width={80}
                      ariaLabel="Quote Number"
                    />
                    <Label width={64}>Revision</Label>
                    <ReadValue
                      value={inv && inv.revisionNumber ? String(inv.revisionNumber) : undefined}
                      mono
                      ariaLabel="Revision Number"
                    />
                  </>
                )}
                {/* Deep-link follows the EFFECTIVE pair, so after picking a quote it
                    opens the newly chosen one rather than the still-saved one. */}
                {hasLinkedQuote ? (
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `/order-management/quotes/${linkedQuoteNumber}-${linkedRevisionNumber}`,
                        '_blank',
                        'noopener',
                      )
                    }
                    className="inline-flex items-center justify-center h-6 w-6 shrink-0 rounded text-blue-600 dark:text-blue-400 hover:bg-slate-100 dark:hover:bg-gray-800"
                    aria-label="Open quote in new tab"
                    title={
                      usingFallbackRevision
                        ? `Open quote ${linkedQuoteNumber} at its current revision (${linkedRevisionNumber}) in a new tab — this template pins the quote, not a revision`
                        : 'Open this quote & revision in a new tab'
                    }
                  >
                    <Icon.external className="w-4 h-4" />
                  </button>
                ) : null}
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={fieldEditable('quoteNumber', canEditQuoteNumber)}
                  field="quoteLink"
                  label="Quote # / Revision"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
              </PermissionsContainer>
              <Row className="!items-start">
                <Label width={LABEL_W} className="mt-1">
                  Invoice Comments
                </Label>
                {fieldEditable('invoiceComments', editable) ? (
                  <textarea
                    className={TEXTAREA_CLS}
                    rows={3}
                    value={str(hv('invoiceComments'))}
                    onChange={(e) => uh('invoiceComments', e.target.value)}
                    aria-label="Invoice Comments"
                  />
                ) : (
                  <span
                    className={
                      'flex-1 min-w-0 text-[0.75rem] whitespace-pre-wrap break-words ' +
                      'border border-slate-200 dark:border-gray-700 rounded px-1.5 py-1 min-h-[44px] bg-white dark:bg-gray-900 ' +
                      (inv?.invoiceComments ? 'text-slate-900 dark:text-gray-100' : 'text-slate-400 dark:text-gray-500 italic')
                    }
                    aria-label="Invoice Comments"
                  >
                    {inv?.invoiceComments || PLACEHOLDER}
                  </span>
                )}
                <InvBulkCheckbox
                  enabled={fieldChangeAll}
                  editable={fieldEditable('invoiceComments', editable)}
                  field="invoiceComments"
                  label="Invoice Comments"
                  checkedInv={checkedInv}
                  onToggle={toggleInv}
                />
              </Row>
            </div>

          </div>

          {/* MIDDLE — Rate Comments */}
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                Rate Comments
              </span>
              <InvBulkCheckbox
                enabled={fieldChangeAll}
                editable={fieldEditable('rateComments', editable)}
                field="rateComments"
                label="Rate Comments"
                checkedInv={checkedInv}
                onToggle={toggleInv}
              />
            </div>
            {fieldEditable('rateComments', editable) ? (
              <textarea
                className={
                  'flex-1 w-full min-w-0 min-h-[96px] bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 ' +
                  'text-[0.75rem] text-slate-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y'
                }
                rows={5}
                value={str(hv('rateComments'))}
                onChange={(e) => uh('rateComments', e.target.value)}
                aria-label="Rate Comments"
              />
            ) : (
              <span
                className={
                  'flex-1 block min-w-0 overflow-auto text-[0.75rem] whitespace-pre-wrap break-words ' +
                  'border border-slate-200 dark:border-gray-700 rounded px-1.5 py-1 min-h-[96px] bg-slate-50 dark:bg-gray-800 ' +
                  (d.rateComments ? 'text-slate-900 dark:text-gray-100' : 'text-slate-400 dark:text-gray-500 italic')
                }
                aria-label="Rate Comments"
              >
                {d.rateComments || PLACEHOLDER}
              </span>
            )}
          </div>

          {/* RIGHT — Quote Comments over Quote Internal Notes, read-only off the linked quote */}
          <div className="min-w-0 flex flex-col gap-3">
            <QuoteCommentBox label="Quote Comments" value={quoteComments} />
            <QuoteCommentBox label="Quote Internal Notes" value={quoteInternalNotes} />
          </div>
        </div>
      </div>

      {/* QUOTE CHARGES / LINE ITEMS ────────────────────────────────────────── */}
      <div className="iv-split bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden p-4">
        <div className="iv-row-split">
          {/* RIGHT — Quote Charges (read-only, from the linked quote) */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1.5">
              <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                Quote Charges
                {quoteCharges.length > 0 && (
                  <span className="ml-1.5 font-normal text-slate-400 dark:text-gray-500">
                    — {quoteCharges.length} {quoteCharges.length === 1 ? 'Charge' : 'Charges'}
                  </span>
                )}
              </div>
              {hasLinkedQuote && (
                <button
                  type="button"
                  onClick={() => setQuoteDrawerOpen(true)}
                  className="text-[0.6875rem] text-blue-600 dark:text-blue-400 hover:underline focus:outline-none focus:underline"
                  aria-label={`Open quote ${linkedQuoteNumber}-${linkedRevisionNumber}`}
                  title={
                    usingFallbackRevision
                      ? `This template pins quote ${linkedQuoteNumber} but no revision, so these charges are its current revision (${linkedRevisionNumber}). Click to open it in a drawer.`
                      : 'Open this quote in a drawer'
                  }
                >
                  Quote #: {linkedQuoteNumber}-{linkedRevisionNumber}
                  {usingFallbackRevision && ' (current)'}
                  {quoteSalesRep && ` / ${quoteSalesRep}`}
                </button>
              )}
            </div>

            <div className="iv-charges-scroll border border-slate-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900">
              <table className="w-full text-[0.75rem] table-auto" aria-label="Quote Charges">
                <thead className="bg-slate-50 dark:bg-gray-800">
                  <tr>
                    <TH className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-800">Description</TH>
                    <TH className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-800">Rate Type</TH>
                    <TH className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-800 text-right">Rate</TH>
                    <TH className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-800">Free Time</TH>
                    {showChargeActions && (
                      <TH className="sticky top-0 z-10 bg-slate-50 dark:bg-gray-800 text-right">
                        Action
                      </TH>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {!hasLinkedQuote ? (
                    <tr>
                      <td
                        colSpan={showChargeActions ? 5 : 4}
                        className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                      >
                        {showV2QuoteRef ? v2QuoteRef : 'No linked quote'}
                      </td>
                    </tr>
                  ) : quoteChargesLoading ? (
                    <tr>
                      <td
                        colSpan={showChargeActions ? 5 : 4}
                        className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : quoteChargesError ? (
                    <tr>
                      <td
                        colSpan={showChargeActions ? 5 : 4}
                        className="px-2 py-6 text-center text-[0.75rem] text-red-600 dark:text-red-300"
                      >
                        {quoteChargesError}
                      </td>
                    </tr>
                  ) : !hasBaseRows && quoteCharges.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showChargeActions ? 5 : 4}
                        className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                      >
                        No charges
                      </td>
                    </tr>
                  ) : (
                    <>
                      {showDrayRow && (
                        <tr
                          className={
                            drayMismatch
                              ? 'bg-amber-50 dark:bg-amber-900/30 [&>td]:text-red-700 dark:[&>td]:text-red-300 [&>td]:font-semibold'
                              : 'even:bg-slate-50 dark:even:bg-gray-800/40 hover:bg-slate-100 dark:hover:bg-gray-800'
                          }
                        >
                          <td className={`px-2 py-1 text-slate-900 dark:text-gray-100 border-b ${drayBorder}`}>
                            Drayage
                          </td>
                          <td className={`px-2 py-1 text-slate-900 dark:text-gray-100 border-b ${drayBorder}`}>
                            {PLACEHOLDER}
                          </td>
                          <td className={`px-2 py-1 text-right text-slate-900 dark:text-gray-100 border-b ${drayBorder}`}>
                            {/* The quote's own precision: rounding to whole dollars made
                                this panel disagree with the quote it mirrors, and let a
                                flagged rate mismatch show a figure that looked identical
                                to the invoice's. */}
                            {formatQuoteRate(drayAmt)}
                          </td>
                          <td className={`px-2 py-1 text-slate-900 dark:text-gray-100 border-b ${drayBorder}`}>
                            {PLACEHOLDER}
                          </td>
                          {showChargeActions && (
                            <td className={`px-2 py-1 text-right whitespace-nowrap border-b ${drayBorder}`}>
                              {canAddBaseCharges && !hasBaseDrayRow && (
                                <InvoiceWriteGate isTemplate={isTemplate}>
                                  <button
                                    type="button"
                                    onClick={() => addBaseChargeRow('dray')}
                                    className={ADD_CHARGE_BTN}
                                    aria-label={`Add ${BASE_DRAY_ITEM} to invoice line items`}
                                  >
                                    + Add to Invoice
                                  </button>
                                </InvoiceWriteGate>
                              )}
                            </td>
                          )}
                        </tr>
                      )}
                      {showFscRow && (
                        <tr className="even:bg-slate-50 dark:even:bg-gray-800/40 hover:bg-slate-100 dark:hover:bg-gray-800">
                          <td className={`px-2 py-1 text-slate-900 dark:text-gray-100 border-b ${BASE_SEP_BORDER}`}>
                            {fscAsOf ? `Fuel Surcharge (as of ${fscAsOf})` : 'Fuel Surcharge'}
                          </td>
                          <td className={`px-2 py-1 text-slate-900 dark:text-gray-100 border-b ${BASE_SEP_BORDER}`}>
                            {fscUnit || PLACEHOLDER}
                          </td>
                          <td className={`px-2 py-1 text-right text-slate-900 dark:text-gray-100 border-b ${BASE_SEP_BORDER}`}>
                            {fscPct}
                          </td>
                          <td className={`px-2 py-1 text-slate-900 dark:text-gray-100 border-b ${BASE_SEP_BORDER}`}>
                            {PLACEHOLDER}
                          </td>
                          {showChargeActions && (
                            <td className={`px-2 py-1 text-right whitespace-nowrap border-b ${BASE_SEP_BORDER}`}>
                              {canAddBaseCharges && !hasFuelRow && (
                                <InvoiceWriteGate isTemplate={isTemplate}>
                                  <button
                                    type="button"
                                    onClick={() => addBaseChargeRow('fuel')}
                                    className={ADD_CHARGE_BTN}
                                    aria-label={`Add ${BASE_FUEL_ITEM} to invoice line items`}
                                  >
                                    + Add to Invoice
                                  </button>
                                </InvoiceWriteGate>
                              )}
                            </td>
                          )}
                        </tr>
                      )}
                      {quoteCharges.map((c) => {
                      const sug = suggestionByChargeId.get(c.id);
                      const mm = mismatchByChargeId.get(c.id);
                      return (
                        <tr
                          key={c.id}
                          className={
                            sug || mm
                              ? 'bg-amber-50 dark:bg-amber-900/30 [&>td]:text-red-700 dark:[&>td]:text-red-300 [&>td]:font-semibold'
                              : 'even:bg-slate-50 dark:even:bg-gray-800/40 hover:bg-slate-100 dark:hover:bg-gray-800'
                          }
                        >
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {c.description || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {c.rateType || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-right text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {c.rate || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {c.freeTime
                              ? `${c.freeTime}${c.freeTimeUnit ? ` ${c.freeTimeUnit}` : ''}`
                              : PLACEHOLDER}
                          </td>
                          {showChargeActions && (
                            <td className="px-2 py-1 text-right whitespace-nowrap border-b border-slate-100 dark:border-gray-700">
                              {!mm && (
                                <PermissionsContainer
                                  componentId={INVOICE_WRITE_PERMISSION_ID}
                                  componentName={INVOICE_WRITE_PERMISSION_NAME}
                                >
                                  <button
                                    type="button"
                                    onClick={() => addQuoteChargeRow(c)}
                                    className={sug ? ADD_CHARGE_BTN_SUGGESTED : ADD_CHARGE_BTN}
                                    aria-label={`Add ${c.description || 'charge'} to invoice line items`}
                                  >
                                    + Add to Invoice
                                  </button>
                                </PermissionsContainer>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    </>
                  )}
                </tbody>
              </table>
            </div>
            {hasLinkedQuote &&
              !quoteChargesLoading &&
              !quoteChargesError &&
              (chargeSuggestions.length > 0 || drayMismatch || chassisSkip
                || overrideCallouts.length > 0) && (
                <div className="mt-2 space-y-1" aria-label="Suggested charges">
                  {chargeSuggestions.map((s) => (
                    <div
                      key={`${s.kind}-${s.item}-${s.existingRecno ?? ''}`}
                      className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                    >
                      <Icon.warn className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                      <span className="flex-1">
                        <span className="font-semibold">
                          {s.kind === 'update'
                            ? `${s.item} differs from the calculated charge`
                            : s.kind === 'remove'
                              ? `${s.item} removal suggested`
                              : `${s.item} charge suggested`}
                        </span>
                        {' — '}
                        {/* Linked as the modal links it — "Custom Chassis Rule #41" opens
                            the rule that decided it. */}
                        <ReasonText
                          reason={s.reason}
                          linkTable={s.reasonLinkTable}
                          linkRecno={s.reasonLinkRecno}
                        />
                        {s.estimated && (
                          <span className="font-semibold">
                            {' '}
                            Estimated — {s.estimateBasis}.
                          </span>
                        )}
                      </span>
                      {/* Reject the charge from here. Overrides are keyed on (pro, line
                          item), so this needs no invoice line to attach to — previously
                          declining a suggestion meant adding a blank line purely to have
                          something to override.

                          LC-only (`canOverride`): the suggestion itself stays on screen
                          for everyone, because knowing the order earned a charge is not
                          the same as deciding not to bill it. */}
                      {canOverride && (
                      <button
                        type="button"
                        onClick={() => setOverrideTarget({
                          item: s.item,
                          invoiceRecno: overrideDocForItem.get(s.item.trim().toUpperCase()),
                        })}
                        title={`Override ${s.item} on this pro — record why`}
                        aria-label={`Override ${s.item}`}
                        className="shrink-0 rounded border border-amber-400 px-1.5 py-0 text-[0.6875rem] font-medium hover:bg-amber-200 dark:border-amber-600 dark:hover:bg-amber-800"
                      >
                        Override
                      </button>
                      )}
                    </div>
                  ))}
                  {/* Every override on the pro, billed or not, at every stage — the one
                      place to see which charges carried a human decision without reading
                      down the rows looking for Edit buttons.

                      AMBER once locked, matching the suggestion callouts above. On an
                      open invoice these are settled decisions sitting quietly beneath
                      live ones, and grey is right. On a closed invoice they are the only
                      thing on the panel still worth a second look, and the colour is
                      what makes them findable rather than another grey line. */}
                  {overrideCallouts.map((r) => (
                    <div
                      key={r.item}
                      className={
                        // The SUGGESTED-CHARGE treatment (Lee, 2026-10-19), character
                        // for character with the rows above it — a drifted decision is
                        // the same kind of thing as a charge the engine is proposing:
                        // something on this panel needing an answer before the invoice
                        // can go. A louder colour would rank it above the suggestions
                        // when it sits in the same queue.
                        //
                        // It cannot collide with the `invoiceLocked` wash below, which
                        // is the same amber: drift is only ever reported while the
                        // invoice is OPEN. The engine declines to run on an invoiced
                        // order, so it returns no rejected charges and nothing to
                        // compare a stamp against.
                        r.needsReview
                          ? 'flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                          : invoiceLocked
                          ? 'flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                          : 'flex items-start gap-1.5 rounded border border-slate-300 bg-slate-50 px-2 py-1 text-[0.6875rem] text-slate-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                      }
                    >
                      <span className="flex-1">
                        {/* "not billed" is only true when there is no line. Saying it
                            over a charge the invoice DOES bill would describe the
                            opposite of what the reader is looking at. */}
                        <span className="font-semibold">
                          {r.item}
                          {r.onInvoice ? ' — overridden' : ' — not billed'}
                        </span>
                        {' — '}
                        {r.reason}
                        {/* Said in words as well as colour: the highlight draws the eye,
                            this says what to DO about it. A refusal whose only
                            explanation lives behind a modal nobody opened reads as the
                            save being broken. */}
                        {r.needsReview && (
                          <span className="font-semibold">
                            {r.stampedTotal != null
                              ? ` — agreed at ${money(r.stampedTotal)}, now ${money(r.withheldTotal ?? 0)}. Re-verify to approve.`
                              : ' — this charge has changed since it was agreed. Re-verify to approve.'}
                          </span>
                        )}
                      </span>
                      {/* DECLINED -> BILLED on a charge the invoice carries. An override
                          there is a choice between two numbers, and either one alone is
                          a fact that hides the decision: 289.00 does not say what was
                          turned down, and 232.00 misreports what the customer is charged.
                          Collapses to one figure when there is nothing to compare — a
                          declined charge (nothing billed), an uncaptured gross, or the
                          two agreeing, where an arrow would be noise. */}
                      {(() => {
                        const declined = r.withheldTotal;
                        const billed = r.billedTotal;
                        const both =
                          r.onInvoice && declined != null && billed != null
                          && Math.abs(billed - declined) >= 0.01;
                        if (both) {
                          return (
                            <span
                              className="shrink-0 whitespace-nowrap font-semibold"
                              title={`Estimated ${money(declined!)}; billed ${money(billed!)}`}
                            >
                              {money(declined!)}
                              <span aria-hidden="true">{' \u2192 '}</span>
                              <span className="sr-only"> billed </span>
                              {money(billed!)}
                            </span>
                          );
                        }
                        const only = r.onInvoice ? (billed ?? declined) : declined;
                        return only == null ? null : (
                          <span className="shrink-0 whitespace-nowrap font-semibold">
                            {money(only)}
                          </span>
                        );
                      })()}
                      {r.needsReview && canOverride && (
                        <button
                          type="button"
                          onClick={() => reverifyOverride(r.item)}
                          disabled={overrideSaving}
                          title={`Re-verify ${r.item} at what it is worth now`}
                          aria-label={`Re-verify ${r.item}`}
                          className="shrink-0 rounded border border-amber-400 px-1.5 py-0 text-[0.6875rem] font-medium hover:bg-amber-200 disabled:opacity-50 dark:border-amber-600 dark:hover:bg-amber-800"
                        >
                          Re-verify
                        </button>
                      )}
                      {/* The row itself — who declined it, why, and what it was worth —
                          stays readable without this button; only the way IN to the
                          editing dialog is LC-only. */}
                      {canOverride && (
                        <OverrideEditButton
                          liitem={r.item}
                          title={r.reason}
                          onClick={() => setOverrideTarget({
                            item: r.item,
                            invoiceRecno: overrideDocForItem.get(r.item.trim().toUpperCase()),
                          })}
                        />
                      )}
                    </div>
                  ))}
                  {/* WHY NO CHASSIS FEE. The engine reaches this answer on every run and
                      could previously only show it on a chassis line that was being
                      removed — so on an order with no chassis line, which is the one
                      with nothing else to go on, it said nothing at all.

                      "not suggested", deliberately not "not billed": the override rows
                      above use that for a charge somebody DECLINED, and this is the
                      engine never proposing one. Same distinction the two halves of the
                      panel are for. */}
                  {/* Stood down when a CHASSIS MANAGEMENT removal is already in the
                      list above: that callout says the same thing about a line that
                      exists, which is the more useful half of it. */}
                  {chassisSkip && !chargeSuggestions.some(
                    (s) => s.kind === 'remove' && s.item.trim().toUpperCase() === 'CHASSIS MANAGEMENT',
                  ) && (
                    <div
                      className={
                        invoiceLocked
                          ? 'flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                          : 'flex items-start gap-1.5 rounded border border-slate-300 bg-slate-50 px-2 py-1 text-[0.6875rem] text-slate-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                      }
                    >
                      <span className="flex-1">
                        <span className="font-semibold">
                          CHASSIS MANAGEMENT — not suggested
                        </span>
                        {' — '}
                        <ReasonText
                          reason={chassisSkip}
                          linkTable={calloutEstimate?.chassisSkipLinkTable}
                          linkRecno={calloutEstimate?.chassisSkipLinkRecno}
                        />
                      </span>
                    </div>
                  )}
                  {drayMismatch && (
                    <div className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[0.6875rem] text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                      <Icon.warn className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                      <span>
                        <span className="font-semibold">Drayage rate mismatch</span>
                        {` — invoice ${money(drayMismatch.invoiceRate)} vs quote ${money(drayMismatch.quoteRate)}.`}
                      </span>
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* RIGHT PANEL — Invoice Line Items */}
          <div className="min-w-0">
            {/* The row above the line items: Invoice Reconciliation on the left; the
                invoice's own controls on the right, with the invoice number always at
                the far right and its options just left of it (Lee, 2026-10-23). */}
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              {/* Order-only. Reconciliation projects a real pro's invoice — it reads the
                  order's legs, dates and derived charges — so there is nothing for it
                  to project on a template, or on an order that has no pro yet. Both
                  are exactly `numericPronumber == null`, which `openReconcile` already
                  refuses; rendering the button anyway offered a control that silently
                  did nothing. */}
              {numericPronumber != null && (
              <PermissionsContainer
                componentId={RECONCILE_PERMISSION_ID}
                componentName={RECONCILE_PERMISSION_NAME}
                defaultHidden
                className="inline-flex"
              >
                <button
                  type="button"
                  onClick={openReconcile}
                  className="inline-flex items-center gap-1 rounded border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-[0.6875rem] font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                  title="Reconcile this invoice — its current lines against every charge the order has earned"
                >
                  ⟳ Invoice Reconciliation
                </button>
              </PermissionsContainer>
              )}
              {/* Invoice # — invoice_history.masterpro, the number the customer sees on
                  the invoice. The backend sends it blank until the invoice has actually
                  been invoiced (invhistoryrecno is 0 on a draft), so presence IS the
                  "once it exists" test and no separate `invoiced` check is needed.

                  Invoice Options sits just left of it. The group also renders for an
                  invoice that HAS a history recno but no masterpro on it, so a missing
                  number cannot take the control down with it.

                  Before the invoice exists the same control is Invoice Estimate Options,
                  so the group also renders for a real (numeric) order with a masterpro.
                  It reads SAVED data, so any unsaved edit on the form holds it. */}
              {(invoiceNumber || mainInvHistoryRecno > 0
                || (numericPronumber != null && !createMode && !!String(d.masterPro ?? '').trim()
                    && Number(inv?.billTo ?? 0) > 0)) && (
                <div className="ml-auto flex items-center gap-3" data-testid="invoice-number">
                  <InvoiceOptionsButton
                    invHistoryRecno={mainInvHistoryRecno}
                    billto={Number(inv?.billTo ?? 0)}
                    invoiceDate={String(inv?.invoiceDate ?? '')}
                    invoiceNumber={invoiceNumber}
                    title={invoiceNumber ? `Invoice ${invoiceNumber}` : 'Invoice Options'}
                    masterpro={numericPronumber != null && !createMode ? String(d.masterPro ?? '') : ''}
                    unsaved={lineItemsDirty || Object.keys(editing?.editedOrderFields ?? {}).length > 0}
                  />
                  {invoiceNumber && (
                    <span className="text-[0.8125rem] font-semibold text-slate-900 dark:text-gray-100">
                      Invoice #: <span className="tabular-nums">{invoiceNumber}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">
                Invoice Line Items
              </div>
              <div className="text-[0.71875rem] text-slate-700 dark:text-gray-300">
                <span className="font-semibold">Invoice Total:</span>{' '}
                <span
                  className={
                    liveInvoiceTotal > 0
                      ? 'text-slate-900 dark:text-gray-100 font-semibold'
                      : 'text-slate-400 dark:text-gray-500'
                  }
                  aria-label="Invoice Total"
                >
                  {liveInvoiceTotal > 0 ? money(liveInvoiceTotal) : PLACEHOLDER}
                </span>
              </div>
            </div>

            <div className="border border-slate-200 dark:border-gray-700 rounded overflow-hidden">
              <table
                className="w-full text-[0.75rem] table-auto"
                aria-label="Invoice Line Items"
              >
                <thead className="bg-slate-50 dark:bg-gray-800">
                  <tr>
                    {lineChangeAll && <TH className="text-center w-8">All</TH>}
                    <TH>Line</TH>
                    <TH>Item</TH>
                    <TH>GL Code</TH>
                    <TH className="text-right">Qty</TH>
                    <TH className="text-right">Rate</TH>
                    <TH>Type</TH>
                    <TH>Description</TH>
                    <TH className="text-right">Total</TH>
                    {lineItemsEditable && <TH className="text-right">Actions</TH>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={(lineItemsEditable ? 9 : 8) + (lineChangeAll ? 1 : 0)}
                        className="px-2 py-6 text-center text-[0.75rem] text-slate-400 dark:text-gray-500 italic"
                      >
                        No items
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) =>
                      lineItemsEditable ? (
                        <tr
                          key={row._key}
                          className={
                            reconciledKeys.has(row._key)
                              ? 'bg-blue-200 dark:bg-blue-900/40'
                              : 'hover:bg-slate-50 dark:hover:bg-gray-800'
                          }
                        >
                          {lineChangeAll && (
                            <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 text-center w-8">
                              {row.item ? (
                                <input
                                  type="checkbox"
                                  className="h-3.5 w-3.5"
                                  aria-label={`Apply ${row.item} to all pros`}
                                  checked={checkedLines.has(row._key)}
                                  onChange={() => toggleLine(row)}
                                />
                              ) : null}
                            </td>
                          )}
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-14">
                            {/* Line # is a read-only 1-based position counter (V1
                                $i+1) — never user-editable; the stored linumber is
                                assigned automatically on add. */}
                            <span
                              className="inline-flex items-center justify-end w-full h-6 px-1 text-[0.75rem] text-slate-500 dark:text-gray-400"
                              aria-label="Line number"
                            >
                              {idx + 1}
                            </span>
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-44">
                            <TypeAhead
                              options={liTypeOptions}
                              value={row.item}
                              onChange={(v) => updateRow(row._key, 'item', v)}
                              onSelect={(opt) => {
                                // Pick sets the item and auto-fills GL Code from the
                                // litype's gltype (REGION/SINGLE/WAREHOUSE).
                                const t = opt.data as InvoiceLiType | undefined;
                                setRows((prev) =>
                                  prev.map((r) =>
                                    r._key === row._key
                                      ? { ...r, item: opt.value, glCode: t?.glType || r.glCode }
                                      : r,
                                  ),
                                );
                              }}
                              compact
                              showSearch
                              flagInvalidValue
                              ariaLabel="Item"
                              placeholder="—"
                              // An EMPTY Item blocks the save just as an unrecognised
                              // one does, so it wears the same orange: one visual
                              // vocabulary for "this cell is why Save is refusing".
                              // flagInvalidValue can't cover it — a blank is not an
                              // unrecognised value, it's the absence of one — so the
                              // ring goes on the container instead.
                              className={`w-full${
                                String(row.item ?? '').trim()
                                  ? ''
                                  : ' rounded-lg ring-1 ring-orange-400 dark:ring-orange-500'
                              }`}
                            />
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-24">
                            <select
                              className={glSelectClass(row.glCode)}
                              title={whseGlTitle(row.glCode)}
                              value={row.glCode}
                              onChange={(e) => updateRow(row._key, 'glCode', e.target.value)}
                              aria-label="GL Code"
                            >
                              <option value="">—</option>
                              {row.glCode && !GL_CODE_OPTIONS.includes(row.glCode) && (
                                <option value={row.glCode}>{row.glCode}</option>
                              )}
                              {GL_CODE_OPTIONS.map((code) => (
                                <option key={code} value={code}>
                                  {code}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-16">
                            <input
                              type="number"
                              className="w-full bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 text-right text-[0.75rem] text-slate-900 dark:text-gray-100 h-6"
                              value={row.qty}
                              onChange={(e) =>
                                updateRow(row._key, 'qty', parseFloat(e.target.value) || 0)
                              }
                              aria-label="Qty"
                            />
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-24">
                            {/* A PCT % rate is a whole-number percentage (38.5 =
                                38.5%); show a % adornment so it reads correctly. */}
                            <div className="flex items-center gap-0.5">
                              <input
                                type="number"
                                step="0.01"
                                className="w-full min-w-0 bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 text-right text-[0.75rem] text-slate-900 dark:text-gray-100 h-6"
                                value={row.rate}
                                onChange={(e) =>
                                  updateRow(row._key, 'rate', parseFloat(e.target.value) || 0)
                                }
                                aria-label="Rate"
                              />
                              {isPctRateType(row.rateType) && (
                                <span className="text-[0.6875rem] text-slate-500 dark:text-gray-400">%</span>
                              )}
                            </div>
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-24">
                            <select
                              className="w-full bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 text-[0.75rem] text-slate-900 dark:text-gray-100 h-6"
                              value={row.rateType}
                              onChange={(e) => updateRow(row._key, 'rateType', e.target.value)}
                              aria-label="Rate Type"
                            >
                              <option value="">—</option>
                              {row.rateType && !RATE_TYPE_OPTIONS.includes(row.rateType) && (
                                <option value={row.rateType}>{row.rateType}</option>
                              )}
                              {RATE_TYPE_OPTIONS.map((rt) => (
                                <option key={rt} value={rt}>
                                  {rt}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700">
                            <input
                              type="text"
                              className="w-full bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-600 rounded px-1 text-[0.75rem] text-slate-900 dark:text-gray-100 h-6"
                              value={row.description}
                              onChange={(e) => updateRow(row._key, 'description', e.target.value)}
                              aria-label="Description"
                            />
                          </td>
                          <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 w-20">
                            {/* Total is read-only — always derived from qty × rate
                                (÷100 for PCT %); see computeLineTotal. */}
                            <span
                              className="inline-flex items-center justify-end w-full h-6 px-1 text-right text-[0.75rem] text-slate-900 dark:text-gray-100"
                              aria-label="Total"
                            >
                              {money(row.total)}
                            </span>
                          </td>
                          {/* READ-ONLY FALLBACK. Once an invoice is locked the editable
                              column disappears, and with it the only place an override on
                              a BILLED line was visible — the rejected-charges strip lists
                              charges with no invoice line, so a billed one appears in
                              neither. Pro 5099885's CHASSIS SPLIT was overridden AND
                              billed, and showed nowhere on the order it was decided on.
                              Rewording stays allowed after invoicing, so the Edit control
                              is real here; creating and revoking are refused server-side
                              (409) and are not offered. */}
                          {lineItemsEditable && (
                            <td className="px-1 py-0.5 border-b border-slate-100 dark:border-gray-700 text-right whitespace-nowrap">
                              {/* Right-aligned so the ✕ holds one position down the
                                  column: centred, an overridden row's Edit pushed it
                                  sideways. */}
                              {/* OUTSIDE the write gate, deliberately. An EXISTING override
                                  is a recorded decision, and opening it to read who decided
                                  and why is not a write — gating it hid the decision from
                                  anyone without invoice-write, which is most readers. The
                                  CREATE control below stays gated, because that one does
                                  write.

                                  Creating one is NOT offered here (Lee, 2026-10-23): an
                                  override is recorded from Invoice Reconciliation or a
                                  suggested charge, with the same labelled Override button
                                  both places — no padlock. */}
                              {(() => {
                                const key = (row.item ?? '').trim().toUpperCase();
                                const ov = key ? overrideByItem.get(key) : undefined;
                                return ov ? (
                                  <OverrideEditButton
                                    liitem={row.item}
                                    className="mr-1"
                                    title={`Overridden by ${ov.userid}: ${ov.reason}${ov.comments ? ` (${ov.comments})` : ''} — view or edit`}
                                    onClick={() => setOverrideTarget({ item: row.item, recno: row.recno })}
                                  />
                                ) : null;
                              })()}
                              <InvoiceWriteGate isTemplate={isTemplate} className="inline-flex items-center gap-1">
                                <button
                                  onClick={() => removeRow(row._key)}
                                  className="text-[0.6875rem] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60"
                                  title="Remove line (applied on Save Changes)"
                                  aria-label="Remove line item"
                                >
                                  ✕
                                </button>
                              </InvoiceWriteGate>
                            </td>
                          )}
                        </tr>
                      ) : (
                        <tr key={row._key}>
                          <td className="px-2 py-1 text-slate-500 dark:text-gray-400 border-b border-slate-100 dark:border-gray-700">
                            {idx + 1}
                          </td>
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {row.item || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {/* Approved template: the row reads like a completed invoice
                                (plain text) EXCEPT GL Code, which stays a dropdown — the
                                one field that remains editable after approval. */}
                            {glOnlyLineEdit || glCodeAfterInvoice ? (
                              <PermissionsContainer
                                componentId={GL_CODE_COMPONENT_ID}
                                componentName={GL_CODE_COMPONENT_NAME}
                                // NOT defaultHidden: at level 0 the container renders
                                // nothing and the cell comes up blank, because the
                                // read-only span is the OTHER arm of this ternary.
                                // Un-granted must still SEE the code.
                                defaultDisabled={glCodeAfterInvoice && !glOnlyLineEdit}
                                showHandle={idx === 0}
                              >
                              {glOnlyLineEdit || canEditGlAfterInvoice ? (
                              <select
                                className={glSelectClass(row.glCode)}
                                title={whseGlTitle(row.glCode)}
                                value={row.glCode}
                                onChange={(e) => updateRow(row._key, 'glCode', e.target.value)}
                                aria-label="GL Code"
                              >
                                <option value="">—</option>
                                {row.glCode && !GL_CODE_OPTIONS.includes(row.glCode) && (
                                  <option value={row.glCode}>{row.glCode}</option>
                                )}
                                {GL_CODE_OPTIONS.map((code) => (
                                  <option key={code} value={code}>
                                    {code}
                                  </option>
                                ))}
                              </select>
                              ) : row.glCode ? (
                                // View only — the same red and the same tooltip the
                                // fully read-only grid uses, so the warehouse-code miss
                                // is still visible to someone who cannot fix it.
                                <span
                                  title={whseGlTitle(row.glCode)}
                                  className={whseGlTitle(row.glCode) ? 'font-semibold text-red-700 dark:text-red-300' : undefined}
                                >
                                  {row.glCode}
                                </span>
                              ) : (
                                PLACEHOLDER
                              )}
                              </PermissionsContainer>
                            ) : row.glCode ? (
                              // Read-only grid (invoiced / no edit rights): same red,
                              // same tooltip, so accounting sees the miss too.
                              <span
                                title={whseGlTitle(row.glCode)}
                                className={whseGlTitle(row.glCode) ? 'font-semibold text-red-700 dark:text-red-300' : undefined}
                              >
                                {row.glCode}
                              </span>
                            ) : (
                              PLACEHOLDER
                            )}
                          </td>
                          <td className="px-2 py-1 text-right text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {row.qty || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-right text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {/* PCT % rate shows the whole-number percentage with a
                                % suffix (38.5%); other types show the money rate. */}
                            {row.rate
                              ? isPctRateType(row.rateType)
                                ? `${row.rate}%`
                                : money(row.rate)
                              : PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {row.rateType || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {row.description || PLACEHOLDER}
                          </td>
                          <td className="px-2 py-1 text-right text-slate-900 dark:text-gray-100 border-b border-slate-100 dark:border-gray-700">
                            {money(row.total)}
                          </td>
                          {/* No Actions cell on a read-only row. It used to carry an
                              override's Edit button, because an override on a BILLED line
                              had nowhere else to appear once the invoice locked. It does
                              now: `overrideCallouts` carries every override in the strip
                              under Quote Charges, at every stage. An Edit control beside
                              a frozen figure would read as an offer to change it, so the
                              strip is the only place a locked invoice offers one. */}
                        </tr>
                      ),
                    )
                  )}
                </tbody>
              </table>
            </div>

            {lineItemsEditable && (
              <div className="mt-2">
                <InvoiceWriteGate isTemplate={isTemplate}>
                  <button
                    onClick={addRow}
                    className="text-[0.75rem] px-3 py-1 rounded border border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                    aria-label="Add line item"
                  >
                    + Add Line
                  </button>
                </InvoiceWriteGate>
              </div>
            )}
            {/* Keyed on the EFFECTIVE lock, not on `invoiced` alone: under an admin
                override the grid is editable, and a note still claiming it is locked
                would contradict what the user is looking at. */}
            {editable && invoiced && !lineItemsEditable && (
              <div className="mt-2 text-[0.71875rem] italic text-slate-500 dark:text-gray-400">
                This invoice has been created — line items are locked.
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Header + line-item edits commit through the order-level footer Save
          (no tab-level Save button) — see useOrderEditing.save(). */}

      <QuoteLaneEditorDrawer
        open={quoteDrawerOpen && !!quoteDrawerLane}
        lane={quoteDrawerLane}
        onClose={() => setQuoteDrawerOpen(false)}
        onChanged={() => setQuoteChargesNonce((n) => n + 1)}
      />

      {numericPronumber ? (
        <>
          <RebillCreditListModal
            open={rebillListOpen}
            pronumber={numericPronumber}
            refreshKey={rebillRefreshKey}
            onClose={() => setRebillListOpen(false)}
            onOpenRecord={(r) => {
              setRebillListOpen(false);
              setRebillEditRecno(r);
              setRebillEditOpen(true);
            }}
          />
          <RebillCreditEditModal
            open={rebillEditOpen}
            pronumber={numericPronumber}
            recno={rebillEditRecno}
            onClose={() => setRebillEditOpen(false)}
            onSaved={() => {
              setRebillEditOpen(false);
              refreshRebillCount();
            }}
          />
        </>
      ) : null}

      {/* Accessorials are their OWN save process (not the footer Save) and work on
          a template too — the modal + its endpoints are template-aware. */}
      {accPro != null && (
        <AccessorialEditModal
          open={accEditOpen}
          pronumber={accPro}
          recno={accEditRecno}
          quoteLookupContext={editing?.quoteLookupContext}
          stagedChanges={accStagedChanges}
          masterPro={String(d.masterPro ?? '')}
          onClose={() => { setAccEditOpen(false); setAccStagedChanges(null); }}
          onSaved={() => {
            setAccEditOpen(false);
            setAccStagedChanges(null);
            refreshAcc();
          }}
        />
      )}

    </div>
  );
};

export default InvoicingView;
